import { Injectable } from '@angular/core';
import { determineTaskActivations } from '@prism/prism-shared';
import { AppwriteStorageService, CompatUploadSnapshot } from '@prism/angular-shared';
import { Store } from '@ngrx/store';
import { AppwriteApiEndpoint, AppwriteApiRoute, cadFileIdToGltfFileId, cadFileNameToGltfFileName, Collection, Contact, DEFAULT_OBSESSED_ENGINEERING_DATA, DEFAULT_OBSESSED_ENGINEERING_STRING_LABEL_CONFIG, DisplayObsessedEngineeringProduct, FactoryAsset, FactoryAssetType, GlobalVariableDocumentIds, MetalsLaserProgramStatus, InventoryTransaction, InventoryTransactionType, MetalsHardwareItem, MetalsHardwareItemType, MetalsLaserMaterial, MetalsQuoteParameters, MetalsLaserProgram, MetalsLaserProgramSchedule, MetalsLaserProgramScheduleShift, MetalsPowderCoatingPowder, MetalsPressBrakeBendRadius, MetalsProcessStation, MetalsProduct, MetalsProductBillOfMaterialsLineItem, MetalsProductBillOfMaterialsLineItemType, MetalsProductSetup, MetalsProductVersion, MetalsProductVersionStatus, MetalsProject, MetalsProjectFile, MetalsProjectFileType, MetalsQuoteRequest, MetalsTask, MetalsTaskStatus, MetalsTaskType, MetalsVendorHardwareData, MetaltrackJob, MetaltrackJobShift, MetaltrackJobStatus, MetaltrackJobStep, MetaltrackSchedule, ObsessedEngineeringComponent, ObsessedEngineeringComponentShipment, ObsessedEngineeringComponentShipmentDamageCamEntry, ObsessedEngineeringComponentShipmentDamageItem, ObsessedEngineeringComponentShipmentItem, ObsessedEngineeringData, ObsessedEngineeringInvoice, ObsessedEngineeringInvoiceStatus, ObsessedEngineeringOrder, ObsessedEngineeringOrderedProduct, ObsessedEngineeringOrderStatus, ObsessedEngineeringOrderType, ObsessedEngineeringProduct, ObsessedEngineeringProductBomLineItem, ObsessedEngineeringProductVersion, ObsessedEngineeringStringLabelConfig, ProductVersionStatus, Resource, StampingStation, StampingTask, StampingTaskStatus, StorageFolder, Tag, ToolRoomMachine, User, MetalsTaskQuantityChangeEvent, MetalsProjectStatus, MetalsOrderedProductStatus, MetalsOrderedProduct, MetalsOrder, MetalsProductType, LabelInstance, LabelInstance_ObsessedEngineeringProduct, LabelType, toDisplayObsessedEngineeringProducts, toEntityDict, MetalsQuote, MetalsInventoryBin, MetalsInventoryRack } from '@prism/prism-shared';
import { Observable, catchError, combineLatest, defaultIfEmpty, defer, filter, first, firstValueFrom, forkJoin, from, map, mergeMap, of, pipe, switchMap, tap, throwError } from 'rxjs';
import { Query, TablesDB } from 'appwrite';
import { CoreSelectors } from '../state/core';
import { PrismCommonApiService } from './prism-common-api.service';
import { SharedApiService } from './shared-api.service';
import { AppwriteDatabaseService, fromAppwriteDoc, fromAppwriteDocs, QueryRef } from '@prism/angular-shared';
import { AppwriteFunctionsService } from './appwrite-functions.service';
import { createOeProductInventoryBatch } from '../../metals/obsessed-engineering/products/oe-product-inventory-batch';


type LabelInstanceWithOrderedProductId = Extract<LabelInstance, { orderedProductId: string }>;

const chunkArray = <T>(items: T[], chunkSize: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += chunkSize) {
        chunks.push(items.slice(i, i + chunkSize));
    }
    return chunks;
};

// @ts-ignore
// import initOpenCascade from 'opencascade.js';


// order list page
// - user has their order lists
// - order lists have statusess

// - user can add plastics, metals, or custom hardware to orderlist
// - user can search history of order lists and reorder, or add to new order
// - submit individual items on an order list
// - order list has name, not associated with an entitiy
// - add order list items to list, manually change status to ready
// - can mark whole order list as complete, disappears into history
// - when item is marked to ready, item sent to order list processing
// - 



@Injectable({ providedIn: 'root' })
export class MetalsApiService {
    private currentUserId: string = ''
    private currentClientDeviceId: string | null = null;

    constructor(
        private store: Store,
        private commonApiService: PrismCommonApiService,
        private storage: AppwriteStorageService,
        private sharedApi: SharedApiService,
        private appwriteDb: AppwriteDatabaseService,
        private functions: AppwriteFunctionsService,
    ) {
        this.store.select(CoreSelectors.getCurrentUser).pipe(
            filter(user => !!user),
            first(),
            tap((user) => {
                this.currentUserId = user.id
            })
        ).subscribe()
        this.store.select(CoreSelectors.getCurrentClientDevice).pipe(
            filter(clientDevice => !!clientDevice),
            first(),
            tap((clientDevice) => {
                this.currentClientDeviceId = clientDevice?.id || null;
            })
        ).subscribe()
    }



    public loadHardwareItems(): Observable<MetalsHardwareItem[]> {
        return this.appwriteDb.collection<MetalsHardwareItem>(Collection.metalsHardwareItem).get().pipe(
            map((resp) => (fromAppwriteDocs(resp.docs))),
        )
    }

    public loadHardwareItemById(hardwareItemId: string): Observable<MetalsHardwareItem> {
        return this.appwriteDb.collection<MetalsHardwareItem>(Collection.metalsHardwareItem).doc(hardwareItemId).get().pipe(
            map((resp) => (fromAppwriteDoc(resp)))
        )
    }

    public loadHardwareItemsByType(
        hardwareItemType: MetalsHardwareItemType
    ): Observable<MetalsHardwareItem[]> {
        return this.appwriteDb.collection<MetalsHardwareItem>(
            Collection.metalsHardwareItem,
            (ref) => ref.where('type', '==', hardwareItemType)
        ).get().pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadHardwareItemsByIds(hardwareItemIds: string[]): Observable<MetalsHardwareItem[]> {
        return this.appwriteDb.listByIds<MetalsHardwareItem>(Collection.metalsHardwareItem, hardwareItemIds);
    }

    public setHardwareItem(hardwareItem: MetalsHardwareItem): Observable<void> {
        return from(this.appwriteDb.collection<MetalsHardwareItem>(Collection.metalsHardwareItem).doc(hardwareItem.id).set(hardwareItem))
    }

    public loadContacts(): Observable<Contact[]> {
        return this.appwriteDb.collection<Contact>(Collection.contact).get().pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadContactById(contactId: string): Observable<Contact> {
        return this.appwriteDb.collection<Contact>(Collection.contact).doc(contactId).get().pipe(
            map((resp) => (fromAppwriteDoc(resp)))
        )
    }

    public setContact(contact: Contact): Observable<void> {
        return from(this.appwriteDb.collection<Contact>(Collection.contact).doc(contact.id).set(contact))
    }

    public loadVendorHardwareData(): Observable<MetalsVendorHardwareData[]> {
        return this.appwriteDb.collection<MetalsVendorHardwareData>(Collection.metalsVendorHardwareData).get().pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public setVendorHardwareData(vendorHardwareData: MetalsVendorHardwareData): Observable<void> {
        return from(this.appwriteDb.collection<MetalsVendorHardwareData>(Collection.metalsVendorHardwareData).doc(vendorHardwareData.id).set(vendorHardwareData))
    }

    /** Load the singleton quote-parameters config (null if it hasn't been saved yet). */
    public loadQuoteParameters(): Observable<MetalsQuoteParameters | null> {
        return this.appwriteDb.collection<MetalsQuoteParameters>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.MetalsQuoteParameters).get().pipe(
                map((resp) => fromAppwriteDoc(resp)),
                catchError(() => of(null)),
            );
    }

    public setQuoteParameters(params: MetalsQuoteParameters): Observable<void> {
        return from(this.appwriteDb.collection<MetalsQuoteParameters>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.MetalsQuoteParameters).set(params));
    }

    public loadObsessedEngineeringData(): Observable<ObsessedEngineeringData> {
        return this.appwriteDb.collection<ObsessedEngineeringData>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.ObsessedEngineeringData).get().pipe(
                map((resp) => {
                    const data = fromAppwriteDoc(resp) as ObsessedEngineeringData & {
                        stringLabelConfig?: ObsessedEngineeringStringLabelConfig;
                    };
                    if (!resp.exists || !data) {
                        return DEFAULT_OBSESSED_ENGINEERING_DATA;
                    }

                    const savedConfigs = Array.isArray(data.stringLabelConfigs)
                        ? data.stringLabelConfigs
                        : data.stringLabelConfig?.labelText
                            ? [data.stringLabelConfig]
                            : [];

                    return {
                        smallLabelPrinterId: data.smallLabelPrinterId || DEFAULT_OBSESSED_ENGINEERING_DATA.smallLabelPrinterId,
                        largeLabelPrinterId: data.largeLabelPrinterId || DEFAULT_OBSESSED_ENGINEERING_DATA.largeLabelPrinterId,
                        oeAddressInfo: data.oeAddressInfo || DEFAULT_OBSESSED_ENGINEERING_DATA.oeAddressInfo,
                        stringLabelConfigs: savedConfigs.map((config) => ({
                            labelText: config.labelText ?? DEFAULT_OBSESSED_ENGINEERING_STRING_LABEL_CONFIG.labelText,
                            rowsPerLabel: config.rowsPerLabel ?? DEFAULT_OBSESSED_ENGINEERING_STRING_LABEL_CONFIG.rowsPerLabel,
                        })),
                        emailContacts: Array.isArray(data.emailContacts)
                            ? data.emailContacts.map((contact) => ({
                                name: contact.name ?? '',
                                email: contact.email ?? '',
                                subscriptions: Array.isArray(contact.subscriptions) ? contact.subscriptions : [],
                            }))
                            : DEFAULT_OBSESSED_ENGINEERING_DATA.emailContacts,
                    };
                }),
                catchError(() => of(DEFAULT_OBSESSED_ENGINEERING_DATA)),
            );
    }

    public setObsessedEngineeringData(data: ObsessedEngineeringData): Observable<void> {
        return from(this.appwriteDb.collection<ObsessedEngineeringData>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.ObsessedEngineeringData).set(data));
    }

    // ---- Metals quotes (persisted quoting module) ----
    public loadMetalsQuotes(): Observable<MetalsQuote[]> {
        return this.appwriteDb.collection<MetalsQuote>(Collection.metalsQuote).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public loadMetalsQuoteById(id: string): Observable<MetalsQuote> {
        return this.appwriteDb.collection<MetalsQuote>(Collection.metalsQuote).doc(id).get().pipe(
            map((resp) => fromAppwriteDoc(resp)),
        );
    }

    public setMetalsQuote(quote: MetalsQuote): Observable<void> {
        return from(this.appwriteDb.collection<MetalsQuote>(Collection.metalsQuote).doc(quote.id).set(quote));
    }

    /** All product versions whose quoteId points at this quote (the version owns the
     *  quote link) — drives the "associated products" display on the quote pages. */
    public loadProductVersionsByQuoteId(quoteId: string): Observable<MetalsProductVersion[]> {
        const query = (ref: QueryRef) => ref.where('quoteId', '==', quoteId);
        return this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion, query).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    /** Realtime stream of one quote (null when it doesn't exist / gets deleted).
     *  Drives the legacy-quote editor's live scan grid while the phone uploads. */
    public loadMetalsQuoteByIdValueChanges(id: string): Observable<MetalsQuote | null> {
        return this.appwriteDb.collection<MetalsQuote>(Collection.metalsQuote).doc(id).valueChanges().pipe(
            map((quote) => quote ?? null),
        );
    }

    public deleteMetalsQuote(id: string): Observable<void> {
        return from(this.appwriteDb.collection<MetalsQuote>(Collection.metalsQuote).doc(id).delete());
    }

    /** Reserve the next quote number from a monotonic counter (only ever increments; never reused). */
    public reserveNextMetalsQuoteNumber(): Observable<number> {
        const ref = this.appwriteDb.collection<{ next: number }>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.MetalsQuoteCounter);
        return from(ref.get()).pipe(
            map((resp) => fromAppwriteDoc<{ next: number }>(resp)),
            catchError(() => of(null)),
            switchMap((current) => {
                const next = current?.next && current.next > 0 ? current.next : 1;
                return from(ref.set({ next: next + 1 } as { next: number })).pipe(map(() => next));
            }),
        );
    }

    /** Upload a quote's DXF to storage; returns the file id (stored on the quote). */
    public uploadQuoteDxf(blob: Blob, fileName: string): Observable<string> {
        const fileId = this.storage.createId();
        return this.storage.upload$(StorageFolder.metalsQuote, fileId, blob, fileName).pipe(map((r) => r.id));
    }

    public getQuoteDxfUrl(fileId: string): string {
        return this.storage.getFileViewUrl(StorageFolder.metalsQuote, fileId);
    }

    /** Upload the selected sheet layout (placements) as a companion JSON file; returns its file id. */
    public uploadQuoteLayout(layout: unknown): Observable<string> {
        const fileId = this.storage.createId();
        const blob = new Blob([JSON.stringify(layout)], { type: 'application/json' });
        return this.storage.upload$(StorageFolder.metalsQuote, fileId, blob, 'layout.json').pipe(map((r) => r.id));
    }

    /** Upload a quote PDF attachment to the metals-quote bucket; returns its file id. */
    public uploadQuoteAttachment(blob: Blob, fileName: string): Observable<string> {
        const fileId = this.storage.createId();
        return this.storage.upload$(StorageFolder.metalsQuote, fileId, blob, fileName).pipe(map((r) => r.id));
    }

    /** Fetch any file in the metals-quote bucket with the Appwrite session attached (see fetchQuoteDxf). */
    public fetchQuoteFile(fileId: string): Promise<Response> {
        return this.fetchQuoteDxf(fileId);
    }

    /** Delete any file in the metals-quote bucket (DXF or layout). */
    public deleteQuoteFile(fileId: string): Observable<void> {
        return this.storage.delete$(StorageFolder.metalsQuote, fileId);
    }

    /**
     * Fetch a stored quote DXF with the Appwrite session attached. A bare fetch
     * of the file-view URL returns 401 in cross-domain / cookie-fallback setups
     * (the session cookie isn't sent), so replicate what the Appwrite SDK does:
     * forward the localStorage cookie-fallback as the X-Fallback-Cookies header.
     */
    public fetchQuoteDxf(fileId: string): Promise<Response> {
        const headers: Record<string, string> = {};
        try {
            const fallback = typeof localStorage !== 'undefined' ? localStorage.getItem('cookieFallback') : null;
            if (fallback && fallback !== '[]') headers['X-Fallback-Cookies'] = fallback;
        } catch { /* localStorage unavailable — fall back to cookies only */ }
        return fetch(this.getQuoteDxfUrl(fileId), { credentials: 'include', headers });
    }

    public deleteQuoteDxf(fileId: string): Observable<void> {
        return this.storage.delete$(StorageFolder.metalsQuote, fileId);
    }

    public loadTags(): Observable<Tag[]> {
        return this.appwriteDb.collection<Tag>(Collection.tag).get().pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadVendorHardwareDataByHardwareItemId(hardwareItemId: string): Observable<MetalsVendorHardwareData[]> {
        return from(this.appwriteDb.collection<MetalsVendorHardwareData>(Collection.metalsVendorHardwareData, ref => ref.where('parentHardwareItemId', '==', hardwareItemId)).get()).pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadVendorHardwareDataByHardwareItemIds(hardwareItemIds: string[]): Observable<MetalsVendorHardwareData[]> {
        return this.appwriteDb.listByFieldValues<MetalsVendorHardwareData>(Collection.metalsVendorHardwareData, 'parentHardwareItemId', hardwareItemIds);
    }

    public getMetalsHardwareItemCount(): Observable<number> {
        return this.appwriteDb.collection<MetalsHardwareItem>(Collection.metalsHardwareItem).count();
    }


    public newHardwareInventoryTransaction(
        hardwareItemId: string,
        changeQty: number,
        comment: string,
        curentUser: User,
        processId: string | null = null,
    ): Observable<InventoryTransaction> {

        const transaction: InventoryTransaction = {
            id: this.appwriteDb.createId(),
            entityId: hardwareItemId,
            change: changeQty,
            comment,
            createdOn: new Date(),
            createdById: curentUser?.id || "",
            type: InventoryTransactionType.MetalsHardware,
        }

        const hardwareItemUpdate$: Observable<void> = this.loadHardwareItemById(hardwareItemId).pipe(
            switchMap((hardwareItem) => from(this.appwriteDb.collection<MetalsHardwareItem>(Collection.metalsHardwareItem).doc(hardwareItemId).update({
                qtyOnHand: (hardwareItem.qtyOnHand ?? 0) + changeQty,
            })))
        );

        const transactionCreate$: Observable<void> = this.sharedApi.setInventoryTransaction(transaction);

        return forkJoin([hardwareItemUpdate$, transactionCreate$]).pipe(
            map(() => transaction)
        )
    }

    public getInventoryTransactionsByHardwareItemId(hardwareItemId: string): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityId(hardwareItemId, InventoryTransactionType.MetalsHardware);
    }

    public loadRecentHardwareInventoryTransactions(): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionRecentByType(InventoryTransactionType.MetalsHardware);
    }

    public setProduct(product: MetalsProduct): Observable<void> {
        return from(this.appwriteDb.collection<MetalsProduct>(Collection.metalsProduct).doc(product.id).set(product))
    }

    public setProductVersion(productVersion: MetalsProductVersion): Observable<void> {
        return from(this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion).doc(productVersion.id).set(productVersion))
    }

    public setProductVersions(productVersions: MetalsProductVersion[]): Observable<void> {
        return forkJoin(productVersions.map((productVersion) => this.setProductVersion(productVersion))).pipe(
            map(() => void 0),
            defaultIfEmpty(void 0),
        );
    }


    public setBillOfMaterialsLineItems(lineItems: MetalsProductBillOfMaterialsLineItem[]): Observable<void[]> {
        const lineItems$ = lineItems.map(lineItem => from(
            this.appwriteDb.collection<MetalsProductBillOfMaterialsLineItem>(
                Collection.metalsProductBillOfMaterialsLineItem
            ).doc(lineItem.id).set(lineItem)));
        return forkJoin(lineItems$).pipe(
            defaultIfEmpty([])
        )
    }

    public deleteBillOfMaterialsLineItemsByIds(lineItemIds: string[]): Observable<void[]> {
        const lineItems$ = lineItemIds.map(lineItemId => from(
            this.appwriteDb.collection<MetalsProductBillOfMaterialsLineItem>(
                Collection.metalsProductBillOfMaterialsLineItem,
            ).doc(lineItemId).delete()));
        return forkJoin(lineItems$).pipe(
            defaultIfEmpty([])
        )
    }

    public getReleasedProductVersionCount(productId: string) {
        const query = (ref: QueryRef) => ref
            .where('productId', '==', productId)
            .where('status', 'in', [MetalsProductVersionStatus.Released, MetalsProductVersionStatus.Obsolete])
        return from(this.appwriteDb.collection(Collection.metalsProductVersion, query).get()).pipe(
            map((resp) => resp.docs.length)
        );
    }

    public getProductCountByCustomerId(customerId: string) {
        const query = (ref: QueryRef) => ref
            .where('customerId', '==', customerId)
            .where('status', 'in', [MetalsProductVersionStatus.Released])
        return from(this.appwriteDb.collection(Collection.metalsProductVersion, query).get()).pipe(
            map((resp) => resp.docs.length)
        );
    }

    public getProductCount() {
        return from(this.appwriteDb.collection(Collection.metalsProduct).get()).pipe(
            map((resp) => resp.docs.length)
        );
    }


    public setCurrentReleasedProductVersionToObsolete(productId: string) {
        const query = (ref: QueryRef) => ref
            .where('productId', '==', productId)
            .where('status', '==', MetalsProductVersionStatus.Released)
        return from(this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion, query).get()).pipe(
            map((resp) => (fromAppwriteDocs(resp.docs))),
            switchMap((productVersions) => {
                const updates$ = (productVersions as MetalsProductVersion[]).map((v) => this.appwriteDb.collection(Collection.metalsProductVersion).doc(v.id).update({
                    status: MetalsProductVersionStatus.Obsolete
                }))
                return forkJoin(updates$).pipe(
                    defaultIfEmpty([])
                )
            })
        );
    }

    public deleteProductVersion(productVersionId: string): Observable<void> {
        return from(this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion).doc(productVersionId).delete())
    }




    public loadProducts(): Observable<MetalsProduct[]> {
        return this.appwriteDb.collection<MetalsProduct>(Collection.metalsProduct).get().pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadProductVersions(): Observable<MetalsProductVersion[]> {
        return this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion).get().pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadReleasedProductVersions(): Observable<MetalsProductVersion[]> {
        return from(this.appwriteDb.collection<MetalsProductVersion>(
            Collection.metalsProductVersion,
            ref => ref.where('status', '==', MetalsProductVersionStatus.Released)).get()).pipe(
                map((resp) => (fromAppwriteDocs(resp.docs)))
            )
    }

    public loadReleasedAndDraftProductVersions(): Observable<MetalsProductVersion[]> {
        return from(this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion,
            ref => ref.where('status', 'in', [MetalsProductVersionStatus.Released, MetalsProductVersionStatus.Draft])
        ).get()).pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadProductsByIds(productIds: string[]): Observable<MetalsProduct[]> {
        return this.appwriteDb.listByIds<MetalsProduct>(Collection.metalsProduct, productIds);
    }

    public loadProductById(productId: string): Observable<MetalsProduct> {
        return this.appwriteDb.collection<MetalsProduct>(Collection.metalsProduct).doc(productId).get().pipe(
            map((resp) => (fromAppwriteDoc(resp)))
        )
    }

    // ===== metals inventory (racks of bins) =====
    // The bin quantity is the product's inventory count; MetalsProduct.qtyOnHand is not used.
    // Every change also writes a MetalsProduct inventory transaction for history.

    public loadInventoryRacks(): Observable<MetalsInventoryRack[]> {
        return this.appwriteDb.collection<MetalsInventoryRack>(Collection.metalsInventoryRack).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        );
    }

    public loadInventoryBinsByRackId(rackId: string): Observable<MetalsInventoryBin[]> {
        return this.appwriteDb.collection<MetalsInventoryBin>(
            Collection.metalsInventoryBin,
            ref => ref.where('rackId', '==', rackId),
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        );
    }

    public loadInventoryBinsByProductId(productId: string): Observable<MetalsInventoryBin[]> {
        return this.appwriteDb.collection<MetalsInventoryBin>(
            Collection.metalsInventoryBin,
            ref => ref.where('productId', '==', productId),
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        );
    }

    public setInventoryRack(rack: MetalsInventoryRack): Observable<void> {
        return from(this.appwriteDb.collection<MetalsInventoryRack>(Collection.metalsInventoryRack).doc(rack.id).set(rack));
    }

    /** Put a product into an empty bin. Counting parts in is a separate step (adjustInventoryBin). */
    public assignProductToInventoryBin(bin: MetalsInventoryBin, productId: string): Observable<void> {
        return from(this.appwriteDb.collection<MetalsInventoryBin>(Collection.metalsInventoryBin).doc(bin.id).update({ productId }));
    }

    /** Add (+) or take (-) parts from a bin. The count is incremented server side and can't go below 0. */
    public adjustInventoryBin(
        bin: MetalsInventoryBin,
        change: number,
        comment: string,
        currentUser: User | null,
    ): Observable<void> {
        if (!bin.productId || change === 0) return of(undefined);
        return this.appwriteDb.batchSetAndIncrementAcrossCollectionsTransactional(
            { [Collection.inventoryTransaction]: [this.newMetalsProductInventoryTransaction(bin.productId, change, comment, currentUser)] },
            [{ collection: Collection.metalsInventoryBin, id: bin.id, field: 'quantity', value: change, min: 0 }],
        );
    }

    /** Empty a bin; whatever count is left is logged as removed. */
    public clearInventoryBin(bin: MetalsInventoryBin, comment: string, currentUser: User | null): Observable<void> {
        const transactions = bin.productId && bin.quantity
            ? [this.newMetalsProductInventoryTransaction(bin.productId, -bin.quantity, comment, currentUser)]
            : [];
        return this.appwriteDb.batchSetAcrossCollectionsTransactional({
            [Collection.metalsInventoryBin]: [{ ...bin, productId: '', quantity: 0 }],
            [Collection.inventoryTransaction]: transactions,
        });
    }

    private newMetalsProductInventoryTransaction(
        productId: string,
        change: number,
        comment: string,
        currentUser: User | null,
    ): InventoryTransaction {
        return {
            id: this.appwriteDb.createId(),
            entityId: productId,
            change,
            comment,
            createdOn: new Date(),
            createdById: currentUser?.id || '',
            type: InventoryTransactionType.MetalsProduct,
        };
    }

    public loadObsessedEngineeringComponents(): Observable<ObsessedEngineeringComponent[]> {
        return this.appwriteDb.collection<ObsessedEngineeringComponent>(Collection.obsessedEngineeringComponent).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public loadObsessedEngineeringComponentById(componentId: string): Observable<ObsessedEngineeringComponent> {
        return this.appwriteDb.collection<ObsessedEngineeringComponent>(Collection.obsessedEngineeringComponent).doc(componentId).get().pipe(
            map((resp) => fromAppwriteDoc(resp)),
        );
    }

    public loadObsessedEngineeringComponentsByIds(componentIds: string[]): Observable<ObsessedEngineeringComponent[]> {
        return componentIds.length
            ? this.appwriteDb.listByIds<ObsessedEngineeringComponent>(Collection.obsessedEngineeringComponent, componentIds)
            : of([]);
    }

    public getObsessedEngineeringComponentCount(): Observable<number> {
        return this.appwriteDb.collection<ObsessedEngineeringComponent>(Collection.obsessedEngineeringComponent).count();
    }

    public createObsessedEngineeringComponent(component: ObsessedEngineeringComponent): Observable<void> {
        return this.appwriteDb.create(Collection.obsessedEngineeringComponent, component);
    }

    public setObsessedEngineeringComponent(component: ObsessedEngineeringComponent): Observable<void> {
        return this.appwriteDb.set(Collection.obsessedEngineeringComponent, component.id, component);
    }

    public newObsessedEngineeringComponentInventoryTransaction(
        componentId: string,
        changeQty: number,
        comment: string,
        currentUser: User,
    ): Observable<InventoryTransaction> {
        const transaction: InventoryTransaction = {
            id: this.appwriteDb.createId(),
            entityId: componentId,
            change: changeQty,
            comment,
            createdOn: new Date(),
            createdById: currentUser.id,
            type: InventoryTransactionType.ObsessedEngineeringComponent,
        };

        const componentUpdate$ = this.loadObsessedEngineeringComponentById(componentId).pipe(
            switchMap((component) => this.setObsessedEngineeringComponent({
                ...component,
                qtyOnHand: component.qtyOnHand + changeQty,
            })),
        );

        return forkJoin([
            componentUpdate$,
            this.sharedApi.setInventoryTransaction(transaction),
        ]).pipe(
            map(() => transaction),
        );
    }

    public loadRecentObsessedEngineeringComponentInventoryTransactions(): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionRecentByType(InventoryTransactionType.ObsessedEngineeringComponent);
    }

    public loadRecentObsessedEngineeringComponentInventoryTransactionsById(
        componentId: string,
        limit: number = 100,
    ): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityIdRecent(
            componentId,
            InventoryTransactionType.ObsessedEngineeringComponent,
            limit,
        );
    }

    public loadObsessedEngineeringComponentInventoryTransactionsById(componentId: string): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityId(
            componentId,
            InventoryTransactionType.ObsessedEngineeringComponent,
        );
    }

    // ============================
    // Obsessed Engineering Component Shipments
    // ============================

    public loadObsessedEngineeringComponentShipments(): Observable<ObsessedEngineeringComponentShipment[]> {
        return this.appwriteDb.collection<ObsessedEngineeringComponentShipment>(Collection.obsessedEngineeringComponentShipment).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public setObsessedEngineeringComponentShipment(shipment: ObsessedEngineeringComponentShipment): Observable<void> {
        return this.appwriteDb.set(Collection.obsessedEngineeringComponentShipment, shipment.id, shipment);
    }

    public loadObsessedEngineeringComponentShipmentById(shipmentId: string): Observable<ObsessedEngineeringComponentShipment> {
        return this.appwriteDb.collection<ObsessedEngineeringComponentShipment>(Collection.obsessedEngineeringComponentShipment).doc(shipmentId).get().pipe(
            map((resp) => fromAppwriteDoc(resp)),
        );
    }

    public deleteObsessedEngineeringComponentShipmentById(shipmentId: string): Observable<void> {
        return from(this.appwriteDb.collection<ObsessedEngineeringComponentShipment>(Collection.obsessedEngineeringComponentShipment).doc(shipmentId).delete());
    }

    public loadObsessedEngineeringComponentShipmentItemsByShipmentId(shipmentId: string): Observable<ObsessedEngineeringComponentShipmentItem[]> {
        return this.appwriteDb.collection<ObsessedEngineeringComponentShipmentItem>(Collection.obsessedEngineeringComponentShipmentItem, (ref) => ref
            .where('oeComponentShipmentId', '==', shipmentId)
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public loadObsessedEngineeringComponentShipmentDamageItemsByShipmentId(shipmentId: string): Observable<ObsessedEngineeringComponentShipmentDamageItem[]> {
        return this.appwriteDb.collection<ObsessedEngineeringComponentShipmentDamageItem>(Collection.obsessedEngineeringComponentShipmentDamageItem, (ref) => ref
            .where('oeComponentShipmentId', '==', shipmentId)
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public deleteObsessedEngineeringComponentShipmentDamageItemsByShipmentId(shipmentId: string): Observable<void[]> {
        return this.loadObsessedEngineeringComponentShipmentDamageItemsByShipmentId(shipmentId).pipe(
            switchMap((items) => items.length
                ? forkJoin(items.map((item) => this.appwriteDb.collection<ObsessedEngineeringComponentShipmentDamageItem>(Collection.obsessedEngineeringComponentShipmentDamageItem).doc(item.id).delete()))
                : of([])
            ),
        );
    }

    public setObsessedEngineeringComponentShipmentDamageCamData(values: ObsessedEngineeringComponentShipmentDamageCamEntry[]): Observable<void> {
        return from(this.appwriteDb.collection<{ values: ObsessedEngineeringComponentShipmentDamageCamEntry[] }>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.ObsessedEngineeringComponentShipmentDamageCAMData)
            .set({ values }));
    }

    public deleteUserEntryForDamageItemFromObsessedEngineeringComponentShipmentDamageCamData(userId: string, damageItemId: string): Observable<void> {
        return this.loadObsessedEngineeringComponentShipmentDamageCamData().pipe(
            switchMap((data) => this.setObsessedEngineeringComponentShipmentDamageCamData(
                data.filter((entry) => entry.userId !== userId || entry.damageItemId !== damageItemId),
            )),
        );
    }

    public deleteUserEntriesForObsessedEngineeringComponentShipmentFromDamageCamData(userId: string, shipmentId: string): Observable<void> {
        return this.loadObsessedEngineeringComponentShipmentDamageCamData().pipe(
            switchMap((data) => this.setObsessedEngineeringComponentShipmentDamageCamData(
                data.filter((entry) => entry.userId !== userId || entry.oeComponentShipmentId !== shipmentId),
            )),
        );
    }

    public getObsessedEngineeringComponentShipmentDamageCamDataValueChanges(): Observable<ObsessedEngineeringComponentShipmentDamageCamEntry[]> {
        const doc = this.appwriteDb.collection<{ values: ObsessedEngineeringComponentShipmentDamageCamEntry[] }>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.ObsessedEngineeringComponentShipmentDamageCAMData);

        return doc.valueChanges().pipe(
            catchError((error) => {
                if (!this.isAppwriteNotFound(error)) return throwError(() => error);
                return this.setObsessedEngineeringComponentShipmentDamageCamData([]).pipe(
                    switchMap(() => doc.valueChanges()),
                );
            }),
            map((resp) => resp?.values || []),
        );
    }

    public loadObsessedEngineeringComponentShipmentDamageCamData(): Observable<ObsessedEngineeringComponentShipmentDamageCamEntry[]> {
        return this.appwriteDb.get<{ values: ObsessedEngineeringComponentShipmentDamageCamEntry[] }>(
            Collection.globalVariables,
            GlobalVariableDocumentIds.ObsessedEngineeringComponentShipmentDamageCAMData,
        ).pipe(
            map((resp) => resp?.values || []),
            catchError((error) => this.isAppwriteNotFound(error) ? of([]) : throwError(() => error)),
        );
    }

    private isAppwriteNotFound(error: unknown): boolean {
        return typeof error === 'object'
            && error !== null
            && 'code' in error
            && (error as { code?: number }).code === 404;
    }

    public loadObsessedEngineeringProductParents(): Observable<ObsessedEngineeringProduct[]> {
        return this.appwriteDb.collection<ObsessedEngineeringProduct>(Collection.obsessedEngineeringProduct).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public loadObsessedEngineeringProductById(productId: string): Observable<ObsessedEngineeringProduct> {
        return this.appwriteDb.collection<ObsessedEngineeringProduct>(Collection.obsessedEngineeringProduct).doc(productId).get().pipe(
            map((resp) => fromAppwriteDoc(resp)),
        );
    }

    public loadObsessedEngineeringProducts(): Observable<DisplayObsessedEngineeringProduct[]> {
        return forkJoin({
            products: this.loadObsessedEngineeringProductParents(),
            productVersions: this.loadReleasedObsessedEngineeringProductVersions(),
        }).pipe(
            map(({ products, productVersions }) => toDisplayObsessedEngineeringProducts(products, productVersions)),
        );
    }

    public loadObsessedEngineeringProductsByIds(productIds: string[]): Observable<DisplayObsessedEngineeringProduct[]> {
        const ids = [...new Set(productIds.filter(Boolean))];
        if (!ids.length) return of([]);

        return forkJoin({
            products: this.appwriteDb.listByIds<ObsessedEngineeringProduct>(Collection.obsessedEngineeringProduct, ids),
            productVersions: this.appwriteDb.listByFieldValues<ObsessedEngineeringProductVersion>(
                Collection.obsessedEngineeringProductVersion,
                'productId',
                ids,
            ),
        }).pipe(
            map(({ products, productVersions }) => toDisplayObsessedEngineeringProducts(
                products,
                productVersions.filter((version) => version.status === ProductVersionStatus.Released),
            )),
        );
    }

    public loadObsessedEngineeringProductsByPartNumbers(partNumbers: string[]): Observable<DisplayObsessedEngineeringProduct[]> {
        const normalizedPartNumbers = [...new Set(partNumbers.map((partNumber) => partNumber.toUpperCase()).filter(Boolean))];
        if (!normalizedPartNumbers.length) return of([]);

        return this.appwriteDb.listByFieldValues<ObsessedEngineeringProductVersion>(
            Collection.obsessedEngineeringProductVersion,
            'partNumber',
            normalizedPartNumbers,
        ).pipe(
            map((versions) => versions.filter((version) => version.status === ProductVersionStatus.Released)),
            switchMap((versions) => {
                if (!versions.length) return of([]);
                return this.appwriteDb.listByIds<ObsessedEngineeringProduct>(
                    Collection.obsessedEngineeringProduct,
                    versions.map((version) => version.productId),
                ).pipe(
                    map((products) => toDisplayObsessedEngineeringProducts(products, versions)),
                );
            }),
        );
    }

    public getObsessedEngineeringProductCount(): Observable<number> {
        return this.appwriteDb.collection<ObsessedEngineeringProduct>(Collection.obsessedEngineeringProduct).count();
    }

    public createObsessedEngineeringProduct(
        product: ObsessedEngineeringProduct,
        productVersion: ObsessedEngineeringProductVersion,
    ): Observable<void> {
        return this.appwriteDb.create(Collection.obsessedEngineeringProduct, product).pipe(
            switchMap(() => this.appwriteDb.batchSetAcrossCollections({
                [Collection.obsessedEngineeringProductVersion]: [productVersion],
            })),
            map(() => void 0),
        );
    }

    public setObsessedEngineeringProduct(product: ObsessedEngineeringProduct): Observable<void> {
        return this.appwriteDb.set(Collection.obsessedEngineeringProduct, product.id, product);
    }

    public loadReleasedObsessedEngineeringProductVersions(): Observable<ObsessedEngineeringProductVersion[]> {
        return this.appwriteDb.collection<ObsessedEngineeringProductVersion>(
            Collection.obsessedEngineeringProductVersion,
            (ref) => ref.where('status', '==', ProductVersionStatus.Released),
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public loadObsessedEngineeringProductVersionsByProductId(productId: string): Observable<ObsessedEngineeringProductVersion[]> {
        return this.appwriteDb.collection<ObsessedEngineeringProductVersion>(
            Collection.obsessedEngineeringProductVersion,
            (ref) => ref.where('productId', '==', productId),
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public loadObsessedEngineeringProductVersionById(productVersionId: string): Observable<ObsessedEngineeringProductVersion> {
        return this.appwriteDb.collection<ObsessedEngineeringProductVersion>(Collection.obsessedEngineeringProductVersion)
            .doc(productVersionId).get().pipe(
                map((resp) => fromAppwriteDoc(resp)),
            );
    }

    public loadObsessedEngineeringProductVersionsByIds(
        productVersionIds: string[],
    ): Observable<ObsessedEngineeringProductVersion[]> {
        return this.appwriteDb.listByIds<ObsessedEngineeringProductVersion>(
            Collection.obsessedEngineeringProductVersion,
            productVersionIds,
        );
    }

    public loadReleasedObsessedEngineeringProductVersionByProductId(productId: string): Observable<ObsessedEngineeringProductVersion> {
        return this.appwriteDb.collection<ObsessedEngineeringProductVersion>(
            Collection.obsessedEngineeringProductVersion,
            (ref) => ref
                .where('productId', '==', productId)
                .where('status', '==', ProductVersionStatus.Released)
                .limit(1),
        ).get().pipe(
            map((resp) => fromAppwriteDocs<ObsessedEngineeringProductVersion>(resp.docs)[0] ?? null),
        );
    }

    public setObsessedEngineeringProductVersion(productVersion: ObsessedEngineeringProductVersion): Observable<void> {
        return this.appwriteDb.set(Collection.obsessedEngineeringProductVersion, productVersion.id, productVersion);
    }

    public loadObsessedEngineeringProductBomLineItemsByProductVersionId(
        productVersionId: string,
    ): Observable<ObsessedEngineeringProductBomLineItem[]> {
        return this.appwriteDb.collection<ObsessedEngineeringProductBomLineItem>(
            Collection.obsessedEngineeringProductBomLineItem,
            (ref) => ref.where('productVersionId', '==', productVersionId),
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public loadObsessedEngineeringProductBomCostData(productVersionIds: string[]): Observable<{
        bomLineItems: ObsessedEngineeringProductBomLineItem[];
        components: ObsessedEngineeringComponent[];
    }> {
        const ids = [...new Set(productVersionIds.filter(Boolean))];
        if (!ids.length) return of({ bomLineItems: [], components: [] });
        return this.appwriteDb.listByFieldValues<ObsessedEngineeringProductBomLineItem>(
            Collection.obsessedEngineeringProductBomLineItem, 'productVersionId', ids,
        ).pipe(
            switchMap((bomLineItems) => this.loadObsessedEngineeringComponentsByIds(
                [...new Set(bomLineItems.map((line) => line.componentId))],
            ).pipe(map((components) => ({ bomLineItems, components })))),
        );
    }

    public loadObsessedEngineeringProductBomLineItemsByComponentId(
        componentId: string,
    ): Observable<ObsessedEngineeringProductBomLineItem[]> {
        return this.appwriteDb.collection<ObsessedEngineeringProductBomLineItem>(
            Collection.obsessedEngineeringProductBomLineItem,
            (ref) => ref.where('componentId', '==', componentId),
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public setObsessedEngineeringProductVersionWithBom(
        productVersion: ObsessedEngineeringProductVersion,
        bomLineItemsToSet: ObsessedEngineeringProductBomLineItem[],
        deletedBomLineItemIds: string[] = [],
    ): Observable<void> {
        return this.appwriteDb.batchSetAndDeleteAcrossCollections({
            [Collection.obsessedEngineeringProductVersion]: [productVersion],
            [Collection.obsessedEngineeringProductBomLineItem]: bomLineItemsToSet,
        }, {
            [Collection.obsessedEngineeringProductBomLineItem]: deletedBomLineItemIds,
        }).pipe(
            map(() => void 0),
        );
    }

    public createObsessedEngineeringProductDraft(
        productVersion: ObsessedEngineeringProductVersion,
        bomLineItems: ObsessedEngineeringProductBomLineItem[],
    ): Observable<void> {
        return this.appwriteDb.batchSetAcrossCollections({
            [Collection.obsessedEngineeringProductVersion]: [productVersion],
            [Collection.obsessedEngineeringProductBomLineItem]: bomLineItems,
        }).pipe(
            map(() => void 0),
        );
    }

    public deleteObsessedEngineeringProductDraft(
        productVersionId: string,
        bomLineItemIds: string[],
    ): Observable<void> {
        return this.appwriteDb.batchSetAndDeleteAcrossCollectionsTransactional({}, {
            [Collection.obsessedEngineeringProductBomLineItem]: bomLineItemIds,
            [Collection.obsessedEngineeringProductVersion]: [productVersionId],
        });
    }

    public releaseObsessedEngineeringProductVersion(
        productVersion: ObsessedEngineeringProductVersion,
        allProductVersions: ObsessedEngineeringProductVersion[],
        currentUser: User,
        releaseComment: string,
    ): Observable<ObsessedEngineeringProductVersion> {
        const historicalVersions = allProductVersions.filter((version) => (
            version.id !== productVersion.id
            && [ProductVersionStatus.Released, ProductVersionStatus.Obsolete].includes(version.status)
        ));
        const nowObsoleteVersions = historicalVersions.map((version) => ({
            ...version,
            status: ProductVersionStatus.Obsolete,
        }));
        const releasedVersion: ObsessedEngineeringProductVersion = {
            ...productVersion,
            status: ProductVersionStatus.Released,
            versionNumber: Math.max(0, ...historicalVersions.map((version) => version.versionNumber ?? 0)) + 1,
            updatedById: currentUser.id,
            updatedOn: new Date(),
            releasedById: currentUser.id,
            releasedOn: new Date(),
            releaseComment,
        };

        return this.appwriteDb.batchSetAcrossCollectionsTransactional({
            [Collection.obsessedEngineeringProductVersion]: [...nowObsoleteVersions, releasedVersion],
        }).pipe(
            map(() => releasedVersion),
        );
    }

    public newObsessedEngineeringProductInventoryTransaction(
        productId: string,
        productVersionId: string,
        changeQty: number,
        comment: string,
        adjustBomInventory: boolean,
    ): Observable<InventoryTransaction> {
        return defer(() => forkJoin({
            product: this.loadObsessedEngineeringProductById(productId),
            version: this.loadObsessedEngineeringProductVersionById(productVersionId),
            bom: adjustBomInventory ? this.loadObsessedEngineeringProductBomLineItemsByProductVersionId(productVersionId) : of([]),
        })).pipe(
            switchMap(input => this.loadObsessedEngineeringComponentsByIds(
                [...new Set(input.bom.map(line => line.componentId))],
            ).pipe(map(components => createOeProductInventoryBatch({
                ...input, components, actorId: this.currentUserId, change: changeQty, comment, adjustBomInventory,
            })))),
            switchMap(batch => this.appwriteDb.batchSetRowsAcrossCollectionsTransactional(batch.request).pipe(
                map(() => batch.transaction),
            )),
        );
    }

    public markObsessedEngineeringOrderReadyToShip(
        order: ObsessedEngineeringOrder,
        orderedProducts: ObsessedEngineeringOrderedProduct[],
        currentUser: User,
    ): Observable<void> {
        const quantityByProductId = orderedProducts.reduce<Record<string, number>>((quantities, orderedProduct) => {
            if (orderedProduct.qty > 0) {
                quantities[orderedProduct.productId] = (quantities[orderedProduct.productId] ?? 0) + orderedProduct.qty;
            }
            return quantities;
        }, {});
        const productIds = Object.keys(quantityByProductId);

        return this.appwriteDb.listByIds<ObsessedEngineeringProduct>(
            Collection.obsessedEngineeringProduct,
            productIds,
        ).pipe(
            switchMap((products) => {
                const loadedProductIds = new Set(products.map((product) => product.id));
                const missingProductIds = productIds.filter((productId) => !loadedProductIds.has(productId));
                if (missingProductIds.length) {
                    return throwError(() => new Error(`Unable to load ordered OE product${missingProductIds.length === 1 ? '' : 's'}: ${missingProductIds.join(', ')}`));
                }

                const transactionGroupId = this.appwriteDb.createId();
                const createdOn = new Date();
                const updatedProducts = products.map((product) => ({
                    ...product,
                    qtyOnHand: product.qtyOnHand - quantityByProductId[product.id],
                }));
                const inventoryTransactions: InventoryTransaction[] = products.map((product) => ({
                    id: this.appwriteDb.createId(),
                    entityId: product.id,
                    change: quantityByProductId[product.id] * -1,
                    comment: `Order ${order.orderNumber} marked ready to ship`,
                    createdOn,
                    createdById: currentUser.id,
                    type: InventoryTransactionType.ObsessedEngineeringProduct,
                    transactionGroupId,
                }));

                return this.appwriteDb.batchSetAcrossCollectionsTransactional({
                    [Collection.obsessedEngineeringOrder]: [order],
                    [Collection.obsessedEngineeringProduct]: updatedProducts,
                    [Collection.inventoryTransaction]: inventoryTransactions,
                });
            }),
        );
    }

    public loadRecentObsessedEngineeringProductInventoryTransactions(): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionRecentByType(InventoryTransactionType.ObsessedEngineeringProduct);
    }

    public loadRecentObsessedEngineeringProductInventoryTransactionsById(
        productId: string,
        limit: number = 100,
    ): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityIdRecent(
            productId,
            InventoryTransactionType.ObsessedEngineeringProduct,
            limit,
        );
    }

    public loadObsessedEngineeringOrders(): Observable<ObsessedEngineeringOrder[]> {
        return this.appwriteDb.collection<ObsessedEngineeringOrder>(Collection.obsessedEngineeringOrder).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        );
    }

    public loadObsessedEngineeringOrdersByStatuses(
        statuses: ObsessedEngineeringOrderStatus[],
        types: ObsessedEngineeringOrderType[] = [],
    ): Observable<ObsessedEngineeringOrder[]> {
        return this.appwriteDb.collection<ObsessedEngineeringOrder>(
            Collection.obsessedEngineeringOrder,
            ref => {
                const statusQuery = ref.where('status', 'in', statuses);
                return types.length ? statusQuery.where('type', 'in', types) : statusQuery;
            },
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public loadObsessedEngineeringOrdersByIds(orderIds: string[]): Observable<ObsessedEngineeringOrder[]> {
        return this.appwriteDb.listByIds<ObsessedEngineeringOrder>(Collection.obsessedEngineeringOrder, orderIds);
    }

    public loadObsessedEngineeringOrderById(orderId: string): Observable<ObsessedEngineeringOrder> {
        return this.appwriteDb.collection<ObsessedEngineeringOrder>(Collection.obsessedEngineeringOrder).doc(orderId).get().pipe(
            map((resp) => fromAppwriteDoc(resp)),
        );
    }

    public loadObsessedEngineeringOrderByIdValueChanges(orderId: string): Observable<ObsessedEngineeringOrder> {
        return this.appwriteDb.collection<ObsessedEngineeringOrder>(Collection.obsessedEngineeringOrder).doc(orderId).valueChanges().pipe(
            map((order) => order || null as unknown as ObsessedEngineeringOrder),
        );
    }

    public getObsessedEngineeringOrderCount(): Observable<number> {
        return this.appwriteDb.collection<ObsessedEngineeringOrder>(Collection.obsessedEngineeringOrder).count();
    }

    public setObsessedEngineeringOrder(order: ObsessedEngineeringOrder): Observable<void> {
        return this.appwriteDb.set(Collection.obsessedEngineeringOrder, order.id, order);
    }

    public deleteObsessedEngineeringOrder(orderId: string): Observable<void> {
        return from(this.appwriteDb.collection<ObsessedEngineeringOrder>(Collection.obsessedEngineeringOrder).doc(orderId).delete());
    }

    public loadObsessedEngineeringInvoicesByOrderId(orderId: string): Observable<ObsessedEngineeringInvoice[]> {
        return this.appwriteDb.collection<ObsessedEngineeringInvoice>(
            Collection.obsessedEngineeringInvoice,
            ref => ref.where('orderId', '==', orderId),
        ).get().pipe(
            map((response) => fromAppwriteDocs(response.docs)
                .sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime())),
        );
    }

    public loadObsessedEngineeringInvoicePdf(fileId: string): Observable<Blob> {
        return this.storage.getFileBlob$(
            StorageFolder.obsessedEngineeringInvoice,
            fileId,
            'application/pdf',
        );
    }

    public createObsessedEngineeringInvoice(
        invoice: ObsessedEngineeringInvoice,
        pdf: Blob,
        pdfFileName: string,
    ): Observable<ObsessedEngineeringInvoice> {
        return from((async () => {
            let rowCreated = false;
            let fileCreated = false;
            const invoicePdfFileId = this.storage.createId();
            try {
                await firstValueFrom(this.storage.upload$(
                    StorageFolder.obsessedEngineeringInvoice,
                    invoicePdfFileId,
                    pdf,
                    pdfFileName,
                ));
                fileCreated = true;

                const savedInvoice: ObsessedEngineeringInvoice = {
                    ...invoice,
                    invoicePdfFileId,
                    invoicePdfDownloadUrl: this.storage.getFileDownloadUrl(
                        StorageFolder.obsessedEngineeringInvoice,
                        invoicePdfFileId,
                    ),
                };
                await firstValueFrom(this.appwriteDb.set(
                    Collection.obsessedEngineeringInvoice,
                    savedInvoice.id,
                    savedInvoice,
                ));
                rowCreated = true;
                const verified = await firstValueFrom(
                    this.appwriteDb.collection<ObsessedEngineeringInvoice>(Collection.obsessedEngineeringInvoice)
                        .doc(savedInvoice.id).get().pipe(map((response) => fromAppwriteDoc(response))),
                );
                if (verified.orderId !== savedInvoice.orderId
                    || verified.invoicePdfFileId !== invoicePdfFileId) {
                    throw new Error('The saved invoice did not match the generated files.');
                }
                return verified;
            } catch (error) {
                if (rowCreated) {
                    await firstValueFrom(this.appwriteDb.delete(Collection.obsessedEngineeringInvoice, invoice.id)).catch(() => undefined);
                }
                if (fileCreated) await firstValueFrom(this.storage.delete$(
                    StorageFolder.obsessedEngineeringInvoice, invoicePdfFileId,
                )).catch(() => undefined);
                throw error;
            }
        })());
    }

    public releaseObsessedEngineeringInvoice(
        orderId: string,
        invoiceId: string,
        releasedById: string,
    ): Observable<ObsessedEngineeringInvoice[]> {
        return this.loadObsessedEngineeringInvoicesByOrderId(orderId).pipe(
            switchMap((invoices) => {
                if (!invoices.some((invoice) => invoice.id === invoiceId)) {
                    return throwError(() => new Error('The selected invoice is no longer available.'));
                }
                const releasedOn = new Date();
                const target = invoices.find((invoice) => invoice.id === invoiceId) as ObsessedEngineeringInvoice;
                const updates = invoices.filter((invoice) => (
                    invoice.id !== invoiceId && invoice.status === ObsessedEngineeringInvoiceStatus.Released
                )).map((invoice): ObsessedEngineeringInvoice => ({
                    ...invoice,
                    status: ObsessedEngineeringInvoiceStatus.Draft,
                    releaseKey: null,
                    releasedOn: null,
                    releasedById: '',
                }));
                updates.push({
                    ...target,
                    status: ObsessedEngineeringInvoiceStatus.Released,
                    releaseKey: orderId,
                    releasedOn,
                    releasedById,
                });
                return this.appwriteDb.batchUpdateAcrossCollectionsTransactional({
                    [Collection.obsessedEngineeringInvoice]: updates,
                }).pipe(switchMap(() => this.loadObsessedEngineeringInvoicesByOrderId(orderId)));
            }),
            map((invoices) => {
                const released = invoices.filter((invoice) => invoice.status === ObsessedEngineeringInvoiceStatus.Released);
                if (released.length !== 1 || released[0].id !== invoiceId) {
                    throw new Error('The released invoice could not be confirmed.');
                }
                return invoices;
            }),
        );
    }

    public loadObsessedEngineeringOrderedProducts(): Observable<ObsessedEngineeringOrderedProduct[]> {
        return this.appwriteDb.collection<ObsessedEngineeringOrderedProduct>(Collection.obsessedEngineeringOrderedProduct).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs)),
        );
    }

    public loadObsessedEngineeringOrderedProductsByOrderIds(
        orderIds: string[],
    ): Observable<ObsessedEngineeringOrderedProduct[]> {
        return this.appwriteDb.listByFieldValues<ObsessedEngineeringOrderedProduct>(
            Collection.obsessedEngineeringOrderedProduct,
            'obsessedEngineeringOrderId',
            orderIds,
        ).pipe(
            map((orderedProducts) => orderedProducts.sort((a, b) => a.createdOn.getTime() - b.createdOn.getTime())),
        );
    }

    public getObsessedEngineeringOrderedProductsByOrderIdValueChanges(
        orderId: string,
    ): Observable<ObsessedEngineeringOrderedProduct[]> {
        return this.appwriteDb.collection<ObsessedEngineeringOrderedProduct>(
            Collection.obsessedEngineeringOrderedProduct,
            ref => ref.where('obsessedEngineeringOrderId', '==', orderId),
        ).valueChanges().pipe(
            map((orderedProducts) => orderedProducts.sort((a, b) => a.createdOn.getTime() - b.createdOn.getTime())),
        );
    }

    public setObsessedEngineeringOrderedProduct(
        orderedProduct: ObsessedEngineeringOrderedProduct,
    ): Observable<void> {
        return this.appwriteDb.set(Collection.obsessedEngineeringOrderedProduct, orderedProduct.id, orderedProduct);
    }

    public deleteObsessedEngineeringOrderedProduct(orderedProductId: string): Observable<void> {
        return this.appwriteDb.delete(Collection.obsessedEngineeringOrderedProduct, orderedProductId);
    }

    public setObsessedEngineeringOrderedProducts(
        orderedProducts: ObsessedEngineeringOrderedProduct[],
    ): Observable<void> {
        return forkJoin(orderedProducts.map((orderedProduct) => this.setObsessedEngineeringOrderedProduct(orderedProduct))).pipe(
            map(() => void 0),
            defaultIfEmpty(void 0),
        );
    }

    public loadProductVersionsByProductId(productId: string): Observable<MetalsProductVersion[]> {
        return from(this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion, ref => ref.where('productId', '==', productId)).get()).pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadBillOfMaterialsLineItemsByProductVersionId(productVersionId: string): Observable<MetalsProductBillOfMaterialsLineItem[]> {
        return from(this.appwriteDb.collection<MetalsProductBillOfMaterialsLineItem>(
            Collection.metalsProductBillOfMaterialsLineItem,
            ref => ref.where('productVersionId', '==', productVersionId)
        ).get()).pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }


    public loadReleasedProductVersionByProductId(productId: string): Observable<MetalsProductVersion> {
        const query = (ref: QueryRef) => ref
            .where('status', '==', MetalsProductVersionStatus.Released)
            .where('productId', '==', productId)
        return from(this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion, query).get()).pipe(
            map(resp => {
                if (!resp.docs.length) {
                    return null as unknown as MetalsProductVersion
                }
                return fromAppwriteDoc(resp.docs[0])
            })
        )
    }

    public loadReleasedProductVersionsByProductIds(productIds: string[]): Observable<MetalsProductVersion[]> {
        return this.appwriteDb.listByFieldValues<MetalsProductVersion>(
            Collection.metalsProductVersion,
            'productId',
            productIds,
            [Query.equal('status', MetalsProductVersionStatus.Released)],
        );
    }

    public loadProductVersionsByIds(productVersionIds: string[]): Observable<MetalsProductVersion[]> {
        return this.appwriteDb.listByIds<MetalsProductVersion>(Collection.metalsProductVersion, productVersionIds);
    }

    public loadProductVersionById(productVersionId: string): Observable<MetalsProductVersion> {
        return this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion).doc(productVersionId).get().pipe(
            map((resp) => (fromAppwriteDoc(resp)))
        )
    }

    /** Packaging follows the current released product revision; a project's source revision stays pinned. */
    public loadPackUnitVersionForProject(project: MetalsProject): Observable<MetalsProductVersion | null> {
        const sourceVersion$ = project.sourceProductVersionId
            ? this.loadProductVersionById(project.sourceProductVersionId).pipe(catchError(() => of(null)))
            : of(null);

        if (project.sourceProductId) {
            return this.loadReleasedProductVersionByProductId(project.sourceProductId).pipe(
                switchMap(released => released ? of(released) : sourceVersion$),
                catchError(() => sourceVersion$),
            );
        }

        return sourceVersion$.pipe(
            switchMap(source => source?.productId
                ? this.loadReleasedProductVersionByProductId(source.productId).pipe(
                    map(released => released || source),
                    catchError(() => of(source)),
                )
                : of(source)
            ),
        );
    }


    public getLaserProgramCount(): Observable<number> {
        return this.appwriteDb.collection<MetalsLaserProgram>(Collection.metalsLaserProgram).count();
    }


    public setLaserProgramScheduleDict(dict: Record<string, MetalsLaserProgramSchedule>): Observable<void> {
        return from(this.appwriteDb.collection(Collection.globalVariables).doc(GlobalVariableDocumentIds.LaserProgramScheduleDict).set(
            this.sanitizeLaserProgramScheduleDict(dict)
        ))
    }

    public getLaserProgramScheduleDictValueChanges(): Observable<Record<string, MetalsLaserProgramSchedule>> {
        return from(this.appwriteDb.collection<Record<string, MetalsLaserProgramSchedule>>(Collection.globalVariables).doc(GlobalVariableDocumentIds.LaserProgramScheduleDict).valueChanges()).pipe(
            map((dict) => dict as Record<string, MetalsLaserProgramSchedule>)
        )
    }

    public loadLaserProgramScheduleDict(): Observable<Record<string, MetalsLaserProgramSchedule>> {
        return this.appwriteDb.collection<Record<string, MetalsLaserProgramSchedule>>(
            Collection.globalVariables,
        ).doc(GlobalVariableDocumentIds.LaserProgramScheduleDict).get().pipe(
            switchMap((resp) => {

                // make the schedule if it doesnt exist
                if (!resp.exists) {
                    return this.commonApiService.loadFactoryAssetsByType(FactoryAssetType.Laser).pipe(
                        switchMap((laserAssets: FactoryAsset[]) => {
                            const dict = laserAssets.reduce((dict: Record<string, MetalsLaserProgramSchedule>, laserAsset) => {
                                dict[laserAsset.id] = {
                                    [MetalsLaserProgramScheduleShift.Day]: [],
                                    [MetalsLaserProgramScheduleShift.Night]: []
                                };
                                return dict;
                            }, {})
                            return this.setLaserProgramScheduleDict(dict).pipe(
                                map(() => dict)
                            )
                        })
                    )
                }
                const dict: Record<string, MetalsLaserProgramSchedule> = resp.data()!;
                return of(this.sanitizeLaserProgramScheduleDict(dict));
            })
        )
    }

    public addToLaserProgramSchedule(programId: string, laserAssetId: string) {
        return this.loadLaserProgramScheduleDict().pipe(
            switchMap((schedule: Record<string, MetalsLaserProgramSchedule>) => {
                if (laserAssetId in schedule) {
                    const updatedSchedule: Record<string, MetalsLaserProgramSchedule> = {
                        ...schedule,
                        [laserAssetId]: {
                            ...schedule[laserAssetId],
                            [MetalsLaserProgramScheduleShift.Day]: [
                                ...schedule[laserAssetId][MetalsLaserProgramScheduleShift.Day],
                                programId
                            ]
                        }
                    }

                    return this.setLaserProgramScheduleDict(updatedSchedule)
                }
                console.log('!!!', laserAssetId, 'not in laser program schedule!!!')
                return of();
            })
        )
    }

    private uniqueProgramIds(programIds: string[]): string[] {
        return [...new Set(programIds.filter(Boolean))];
    }

    private sanitizeLaserProgramScheduleDict(
        dict: Record<string, MetalsLaserProgramSchedule>,
    ): Record<string, MetalsLaserProgramSchedule> {
        return Object.entries(dict).reduce((cleanDict: Record<string, MetalsLaserProgramSchedule>, [laserAssetId, schedule]) => {
            const dayProgramIds = schedule?.[MetalsLaserProgramScheduleShift.Day];
            const nightProgramIds = schedule?.[MetalsLaserProgramScheduleShift.Night];
            if (!Array.isArray(dayProgramIds) || !Array.isArray(nightProgramIds)) {
                return cleanDict;
            }
            cleanDict[laserAssetId] = {
                [MetalsLaserProgramScheduleShift.Day]: this.uniqueProgramIds(dayProgramIds),
                [MetalsLaserProgramScheduleShift.Night]: this.uniqueProgramIds(nightProgramIds),
            };
            return cleanDict;
        }, {});
    }

    private scheduleContainsProgramIds(
        dict: Record<string, MetalsLaserProgramSchedule>,
        laserAssetId: string,
        shift: MetalsLaserProgramScheduleShift,
        programIds: string[],
    ): string[] {
        const scheduleProgramIds = dict[laserAssetId]?.[shift] || [];
        return this.uniqueProgramIds(programIds).filter(programId => !scheduleProgramIds.includes(programId));
    }

    private scheduleContainsProgramId(
        dict: Record<string, MetalsLaserProgramSchedule>,
        programId: string,
    ): boolean {
        return Object.values(dict).some(schedule => {
            return Object.values(MetalsLaserProgramScheduleShift).some(shift => {
                return (schedule?.[shift] || []).includes(programId);
            });
        });
    }

    private appendMissingProgramsToSchedule(
        dict: Record<string, MetalsLaserProgramSchedule>,
        laserAssetId: string,
        shift: MetalsLaserProgramScheduleShift,
        programIds: string[],
    ): Record<string, MetalsLaserProgramSchedule> {
        const currentSchedule = dict[laserAssetId];
        if (!currentSchedule) {
            throw new Error(`Laser ${laserAssetId} is missing from laser program schedule`);
        }

        const currentProgramIds = currentSchedule[shift] || [];
        const missingProgramIds = this.uniqueProgramIds(programIds).filter(programId => !currentProgramIds.includes(programId));

        return {
            ...dict,
            [laserAssetId]: {
                ...currentSchedule,
                [shift]: [
                    ...currentProgramIds,
                    ...missingProgramIds,
                ],
            },
        };
    }

    private removeProgramFromScheduleDict(
        dict: Record<string, MetalsLaserProgramSchedule>,
        programId: string,
    ): Record<string, MetalsLaserProgramSchedule> {
        return Object.entries(dict).reduce((updatedDict: Record<string, MetalsLaserProgramSchedule>, [laserAssetId, schedule]) => {
            updatedDict[laserAssetId] = {
                [MetalsLaserProgramScheduleShift.Day]: (schedule[MetalsLaserProgramScheduleShift.Day] || []).filter(id => id !== programId),
                [MetalsLaserProgramScheduleShift.Night]: (schedule[MetalsLaserProgramScheduleShift.Night] || []).filter(id => id !== programId),
            };
            return updatedDict;
        }, {});
    }

    public verifyLaserProgramsScheduled(
        programIds: string[],
        laserAssetId: string,
        shift: MetalsLaserProgramScheduleShift = MetalsLaserProgramScheduleShift.Day,
    ): Observable<void> {
        const uniqueProgramIds = this.uniqueProgramIds(programIds);

        if (!uniqueProgramIds.length) {
            return of(void 0);
        }

        return this.loadLaserProgramScheduleDict().pipe(
            switchMap((dict) => {
                dict = this.sanitizeLaserProgramScheduleDict(dict);
                const missingProgramIds = this.scheduleContainsProgramIds(dict, laserAssetId, shift, uniqueProgramIds);
                if (!missingProgramIds.length) {
                    return of(void 0);
                }

                const repairedDict = this.appendMissingProgramsToSchedule(dict, laserAssetId, shift, missingProgramIds);
                return this.setLaserProgramScheduleDict(repairedDict).pipe(
                    switchMap(() => this.loadLaserProgramScheduleDict()),
                    switchMap((verifiedDict) => {
                        verifiedDict = this.sanitizeLaserProgramScheduleDict(verifiedDict);
                        const stillMissingProgramIds = this.scheduleContainsProgramIds(verifiedDict, laserAssetId, shift, uniqueProgramIds);
                        if (stillMissingProgramIds.length) {
                            return throwError(() => new Error(`Laser schedule write verification failed for programs: ${stillMissingProgramIds.join(', ')}`));
                        }
                        return of(void 0);
                    })
                );
            })
        );
    }

    public loadLaserProgramScheduleDictWithoutProgram(programId: string): Observable<Record<string, MetalsLaserProgramSchedule>> {
        return this.loadLaserProgramScheduleDict().pipe(
            map((dict) => this.removeProgramFromScheduleDict(this.sanitizeLaserProgramScheduleDict(dict), programId))
        );
    }

    public verifyLaserProgramRemovedFromSchedule(programId: string): Observable<void> {
        return this.loadLaserProgramScheduleDict().pipe(
            switchMap((dict) => {
                dict = this.sanitizeLaserProgramScheduleDict(dict);
                if (!this.scheduleContainsProgramId(dict, programId)) {
                    return of(void 0);
                }

                const repairedDict = this.removeProgramFromScheduleDict(dict, programId);
                return this.setLaserProgramScheduleDict(repairedDict).pipe(
                    switchMap(() => this.loadLaserProgramScheduleDict()),
                    switchMap((verifiedDict) => {
                        verifiedDict = this.sanitizeLaserProgramScheduleDict(verifiedDict);
                        if (this.scheduleContainsProgramId(verifiedDict, programId)) {
                            return throwError(() => new Error(`Laser schedule removal verification failed for program: ${programId}`));
                        }
                        return of(void 0);
                    })
                );
            })
        );
    }



    public getStampingStationsValueChanges(): Observable<StampingStation[]> {
        return this.appwriteDb.collection<StampingStation>(Collection.stampingStation).valueChanges().pipe(
            map((resp) => resp as StampingStation[]),
            map(stations => (stations as StampingStation[]).sort((a, b) => a.name.localeCompare(b.name)))
        )
    }


    public setStampingStation(station: StampingStation): Observable<void> {
        return from(this.appwriteDb.collection<StampingStation>(Collection.stampingStation).doc(station.id).set(station))
    }

    public setStampingTask(task: StampingTask): Observable<void> {
        return from(this.appwriteDb.collection<StampingTask>(Collection.stampingTask).doc(task.id).set(task))
    }

    public loadActiveStampingTasks(): Observable<StampingTask[]> {

        const query = (ref: QueryRef) => ref.where('status', 'in', [StampingTaskStatus.NotStarted, StampingTaskStatus.InProgress]);
        return from(this.appwriteDb.collection<StampingTask>(Collection.stampingTask, query).get()).pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadStampingStations(): Observable<StampingStation[]> {
        return this.appwriteDb.collection<StampingStation>(Collection.stampingStation).get().pipe(
            map((resp) => (fromAppwriteDocs(resp.docs))),
            map(stations => (stations as StampingStation[]).sort((a, b) => a.name.localeCompare(b.name)))
        )
    }

    public loadProductSetups(): Observable<MetalsProductSetup[]> {
        return this.appwriteDb.list<MetalsProductSetup>(Collection.metalsProductSetup, [Query.limit(5000)]).pipe(
            map((resp) => [...resp.items].sort((a, b) => {
                const aTime = a.updatedOn instanceof Date ? a.updatedOn.getTime() : 0;
                const bTime = b.updatedOn instanceof Date ? b.updatedOn.getTime() : 0;
                return bTime - aTime;
            })),
        );
    }

    public loadProductSetupById(productSetupId: string): Observable<MetalsProductSetup> {
        return this.appwriteDb.get<MetalsProductSetup>(Collection.metalsProductSetup, productSetupId);
    }

    /** Live-subscribe to a product setup (phone image uploads appear on desktop). */
    public loadProductSetupByIdValueChanges(productSetupId: string): Observable<MetalsProductSetup | null> {
        return this.appwriteDb.collection<MetalsProductSetup>(Collection.metalsProductSetup).doc(productSetupId).valueChanges().pipe(
            map((setup) => setup ?? null),
        );
    }

    public setProductSetup(productSetup: MetalsProductSetup): Observable<void> {
        return from(this.appwriteDb.collection<MetalsProductSetup>(Collection.metalsProductSetup).doc(productSetup.id).set(productSetup));
    }

    public deleteProductSetup(productSetupId: string): Observable<void> {
        return from(this.appwriteDb.collection<MetalsProductSetup>(Collection.metalsProductSetup).doc(productSetupId).delete());
    }

    /** Upload an operation/product-setup image; returns the storage file id. */
    public uploadProductSetupFile(blob: Blob, fileName: string): Observable<string> {
        const fileId = this.storage.createId();
        return this.storage.upload$(StorageFolder.metalsProductSetup, fileId, blob, fileName).pipe(map((r) => r.id));
    }

    public getProductSetupFileViewUrl(fileId: string): string {
        return this.storage.getFileViewUrl(StorageFolder.metalsProductSetup, fileId);
    }

    public loadToolRoomMachines(): Observable<ToolRoomMachine[]> {
        return this.appwriteDb.list<ToolRoomMachine>(Collection.toolRoomMachine, [Query.limit(5000)]).pipe(
            map((resp) => [...resp.items].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))),
        );
    }

    public setToolRoomMachine(machine: ToolRoomMachine): Observable<void> {
        return from(this.appwriteDb.collection<ToolRoomMachine>(Collection.toolRoomMachine).doc(machine.id).set(machine));
    }

    public deleteToolRoomMachine(machineId: string): Observable<void> {
        return from(this.appwriteDb.collection<ToolRoomMachine>(Collection.toolRoomMachine).doc(machineId).delete());
    }


    public loadReleasedProductVersionDependantDataByProductId(productId: string): Observable<{
        productVersion: MetalsProductVersion;
        lineItems: MetalsProductBillOfMaterialsLineItem[];
        lineItemHardwareItems: MetalsHardwareItem[];
        lineItemProducts: MetalsProduct[];
        lineItemProductVersions: MetalsProductVersion[];
        productDrawingResource: Resource | null;
    }> {
        return this.loadReleasedProductVersionByProductId(productId).pipe(
            mergeMap((productVersion) => {
                return this.loadBillOfMaterialsLineItemsByProductVersionId(productVersion.id).pipe(
                    catchError(error => {
                        console.error('Error loading bill of materials line items:', error);
                        return throwError(() => new Error('Failed to load bill of materials line items.'));
                    }),
                    mergeMap((lineItems) => {
                        const hardwareItemIds = lineItems
                            .filter(li => li.type === MetalsProductBillOfMaterialsLineItemType.Hardware)
                            .map(li => li.entityId);
                        const productIds = lineItems
                            .filter(li => li.type === MetalsProductBillOfMaterialsLineItemType.Product)
                            .map(li => li.entityId);

                        const hardwareItems$ = this.loadHardwareItemsByIds(hardwareItemIds).pipe(
                            catchError(error => {
                                console.error('Error loading hardware items:', error);
                                return of([]); // Return empty array on failure
                            })
                        );

                        const productVersions$ = this.loadReleasedProductVersionsByProductIds(productIds).pipe(
                            catchError(error => {
                                console.error('Error loading product versions:', error);
                                return of([]); // Return empty array on failure
                            })
                        );

                        const products$ = this.loadProductsByIds(productIds).pipe(
                            catchError(error => {
                                console.error('Error loading products:', error);
                                return of([]); // Return empty array on failure
                            })
                        );

                        let productDrawingResource$: Observable<Resource | null> = of(null); // Default to null

                        if (!!productVersion.drawingResourceId) {

                            // load the resource for the product drawing
                            productDrawingResource$ = this.commonApiService.loadProductDrawingResourceById(productVersion.drawingResourceId).pipe(
                                catchError(error => {
                                    console.error('Error loading product drawing resource:', error);
                                    return of(null); // Return null on failure
                                })
                            );

                        }


                        return forkJoin([hardwareItems$, products$, productVersions$, productDrawingResource$]).pipe(
                            catchError(error => {
                                console.error('Error during forkJoin processing:', error);
                                return throwError(() => new Error('Failed to load some dependencies.'));
                            }),
                            map(([lineItemHardwareItems, lineItemProducts, lineItemProductVersions, productDrawingResource]) => {
                                const data = {
                                    productVersion,
                                    lineItems,
                                    lineItemHardwareItems,
                                    lineItemProducts,
                                    lineItemProductVersions,
                                    productDrawingResource
                                }
                                return data;
                            })
                        );
                    })
                )

            })
        )

    }


    public getStampingStationByIdValueChanges(stationId: string): Observable<StampingStation | null> {
        return this.appwriteDb.collection<StampingStation>(Collection.stampingStation).doc(stationId).valueChanges().pipe(
            map((resp) => {
                if (!resp) {
                    return null
                }
                return resp as StampingStation
            }),
        )
    }


    public getPlasticFinishingTaskByIdValueChanges(taskId: string): Observable<StampingTask | null> {
        return this.appwriteDb.collection<StampingTask>(Collection.stampingTask).doc(taskId).valueChanges().pipe(
            map((resp) => {
                if (!resp) {
                    return null
                }
                return resp as StampingTask
            }),
        )
    }


    public getReleasedProductVersionByProductIdValueChanges(productId: string): Observable<MetalsProductVersion> {
        const query = (ref: QueryRef) => ref
            .where('status', '==', MetalsProductVersionStatus.Released)
            .where('productId', '==', productId)
        return from(this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion, query).valueChanges()).pipe(
            map(docs => {
                if (!docs.length) {
                    return null as unknown as MetalsProductVersion;
                }
                return docs[0] as MetalsProductVersion;
            })
        )
    }

    public getBillOfMaterialsLineItemsByHardwareItemId(hardwareItemId: string): Observable<MetalsProductBillOfMaterialsLineItem[]> {
        return from(this.appwriteDb.collection<MetalsProductBillOfMaterialsLineItem>(
            Collection.metalsProductBillOfMaterialsLineItem,
            ref => ref
                .where('entityId', '==', hardwareItemId)
                .where('type', '==', MetalsProductBillOfMaterialsLineItemType.Hardware)
        ).get()).pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        );
    }

    public loadMetaltrackSchedule(): Observable<MetaltrackSchedule> {
        return from(this.appwriteDb.collection<{ schedule: MetaltrackSchedule }>(Collection.globalVariables).doc(GlobalVariableDocumentIds.MetaltrackSchedule).get()).pipe(
            map((resp) => {
                if (resp.exists) {
                    const doc = (fromAppwriteDoc(resp));
                    return doc.schedule;
                }
                return {
                    [MetaltrackJobStep.cutting]: {
                        [MetaltrackJobShift.day]: [],
                        [MetaltrackJobShift.night]: [],
                    },
                    [MetaltrackJobStep.bending]: {
                        [MetaltrackJobShift.day]: [],
                        [MetaltrackJobShift.night]: [],
                    },
                    [MetaltrackJobStep.painting]: {
                        [MetaltrackJobShift.day]: [],
                        [MetaltrackJobShift.night]: [],
                    },
                }

            }),
        )
    }

    public getMetaltrackScheduleValueChanges(): Observable<MetaltrackSchedule> {
        return from(this.appwriteDb.collection<{ schedule: MetaltrackSchedule }>(
            Collection.globalVariables
        ).doc(
            GlobalVariableDocumentIds.MetaltrackSchedule
        ).valueChanges()).pipe(
            map((doc) => {
                if (!!doc) {
                    return doc.schedule;
                }
                return {
                    [MetaltrackJobStep.cutting]: {
                        [MetaltrackJobShift.day]: [],
                        [MetaltrackJobShift.night]: [],
                    },
                    [MetaltrackJobStep.bending]: {
                        [MetaltrackJobShift.day]: [],
                        [MetaltrackJobShift.night]: [],
                    },
                    [MetaltrackJobStep.painting]: {
                        [MetaltrackJobShift.day]: [],
                        [MetaltrackJobShift.night]: [],
                    },
                }

            }),
        )
    }

    public setMetaltrackSchedule(schedule: MetaltrackSchedule): Observable<void> {

        const isEmpty = !schedule ||
            Object.keys(schedule).length === 0 ||
            Object.values(schedule).every(step =>
                (!step[MetaltrackJobShift.day] || step[MetaltrackJobShift.day].length === 0) &&
                (!step[MetaltrackJobShift.night] || step[MetaltrackJobShift.night].length === 0)
            );

        if (isEmpty) {
            console.log('Schedule Empty')
            return of(void 0);
        }
        return from(this.appwriteDb.collection(Collection.globalVariables).doc(GlobalVariableDocumentIds.MetaltrackSchedule).set({ schedule }))
    }

    public setMetaltrackJob(job: MetaltrackJob): Observable<void> {
        return from(this.appwriteDb.collection(Collection.metaltrackJob).doc(job.id).set(job))
    }

    public setMetaltrackJobs(jobs: MetaltrackJob[]): Observable<void> {
        return forkJoin(jobs.map((job) => this.setMetaltrackJob(job))).pipe(
            defaultIfEmpty([]),
            map(() => void 0),
        );
    }

    public deleteMetaltrackJob(job: MetaltrackJob): Observable<void> {
        return from(this.appwriteDb.collection(Collection.metaltrackJob).doc(job.id).delete())
    }

    public loadMetaltrackJobById(jobId: string): Observable<MetaltrackJob> {
        return from(this.appwriteDb.collection<MetaltrackJob>(Collection.metaltrackJob).doc(jobId).get()).pipe(
            map(resp => (fromAppwriteDoc(resp)))
        )
    }

    public loadMetaltrackJobsByIds(jobIds: string[]): Observable<MetaltrackJob[]> {
        return this.appwriteDb.listByIds<MetaltrackJob>(Collection.metaltrackJob, jobIds);
    }

    public loadMetaltrackJobs(): Observable<MetaltrackJob[]> {
        return from(this.appwriteDb.collection<MetaltrackJob>(Collection.metaltrackJob).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadCompletedMetaltrackJobs(
        limit: number | null = 500,
    ): Observable<MetaltrackJob[]> {
        if (!limit) {
            return from(this.appwriteDb.collection<MetaltrackJob>(
                Collection.metaltrackJob,
                (ref: QueryRef) => ref
                    .where('status', '==', MetaltrackJobStatus.Completed)
                    .orderBy('completedOn', 'desc')
            ).get()).pipe(
                map(resp => (fromAppwriteDocs(resp.docs)))
            )
        }
        return from(this.appwriteDb.collection<MetaltrackJob>(
            Collection.metaltrackJob,
            (ref: QueryRef) => ref
                .where('status', '==', MetaltrackJobStatus.Completed)
                .orderBy('completedOn', 'desc')
                .limit(limit)
        ).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadMetaltrackJobsByPowderId(): Observable<MetaltrackJob[]> {
        return from(this.appwriteDb.collection<MetaltrackJob>(
            Collection.metaltrackJob,
            (ref: QueryRef) => ref
                .where('powderId', '==', '')
                .orderBy('completedOn', 'desc')
        ).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }


    // public deleteSheetMetalDxfDocuments(documents: SheetMetalDxfDocument[]) {
    //     const documentIds = documents.map(d => d.id);
    //     return this.batchDelete(documentIds,Collection.sheetMetalDxfDocument);
    // }


    public setLaserProgram(laserProgram: MetalsLaserProgram): Observable<void> {
        return from(this.appwriteDb.collection(Collection.metalsLaserProgram).doc(laserProgram.id).set(laserProgram))
    }

    public setLaserProgramExistingOrCreate(laserProgram: MetalsLaserProgram): Observable<void> {
        return from(this.appwriteDb.collection(Collection.metalsLaserProgram).doc(laserProgram.id).set(laserProgram));
    }


    public getLaserProgramsByStatusesValueChanges(
        statuses: MetalsLaserProgramStatus[] = [
            MetalsLaserProgramStatus.Draft,
            MetalsLaserProgramStatus.Released,
        ]
    ): Observable<MetalsLaserProgram[]> {
        return from(this.appwriteDb.collection<MetalsLaserProgram>(
            Collection.metalsLaserProgram,
            (ref) => ref.where('status', 'in', statuses)
        ).valueChanges()).pipe(
            map((tasks) => tasks as MetalsLaserProgram[])
        )
    }

    public loadLaserProgramsByStatuses(
        statuses: MetalsLaserProgramStatus[] = [
            MetalsLaserProgramStatus.Draft,
            MetalsLaserProgramStatus.Released,
        ]
    ): Observable<MetalsLaserProgram[]> {
        return from(this.appwriteDb.collection<MetalsLaserProgram>(
            Collection.metalsLaserProgram,
            (ref) => ref.where('status', 'in', statuses)
        ).get()).pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadRecentCompletedLaserPrograms(
        completedSince: Date,
        limit: number,
    ): Observable<MetalsLaserProgram[]> {
        return this.loadLaserProgramsByStatuses([MetalsLaserProgramStatus.Completed]).pipe(
            map((programs) => programs
                .filter(program => new Date(program.updatedOn).getTime() >= completedSince.getTime())
                .sort((a, b) => new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime())
                .slice(0, limit)
            )
        );
    }

    public loadCompletedLaserProgramsByTaskId(
        taskId: string
    ): Observable<MetalsLaserProgram[]> {
        return from(this.appwriteDb.collection<MetalsLaserProgram>(
            Collection.metalsLaserProgram,
            (ref) => ref
                .where('taskIds', 'array-contains', taskId)
                .where('status', '==', MetalsLaserProgramStatus.Completed)
        ).get()).pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadCompletedLaserProgramsByTaskIds(
        taskIds: string[]
    ): Observable<MetalsLaserProgram[]> {
        const uniqueTaskIds = Array.from(new Set(taskIds.filter(Boolean)));
        const taskIdChunks = chunkArray(uniqueTaskIds, 25);

        if (taskIdChunks.length === 0) {
            return of([]);
        }

        return forkJoin(taskIdChunks.map(ids => from(this.appwriteDb.collection<MetalsLaserProgram>(
            Collection.metalsLaserProgram,
            (ref) => ref
                .where('taskIds', 'array-contains-any', ids)
                .where('status', '==', MetalsLaserProgramStatus.Completed)
                .limit(5000)
        ).get()).pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        ))).pipe(
            defaultIfEmpty([]),
            map(programGroups => Object.values(toEntityDict(programGroups.flat())))
        )
    }

    public getCompletedLaserProgramsByTaskIdsValueChanges(
        taskIds: string[]
    ): Observable<MetalsLaserProgram[]> {
        const uniqueTaskIds = Array.from(new Set(taskIds.filter(Boolean)));
        const taskIdChunks = chunkArray(uniqueTaskIds, 25);

        if (taskIdChunks.length === 0) {
            return of([]);
        }

        return combineLatest(taskIdChunks.map(ids => from(this.appwriteDb.collection<MetalsLaserProgram>(
            Collection.metalsLaserProgram,
            (ref) => ref
                .where('taskIds', 'array-contains-any', ids)
                .where('status', '==', MetalsLaserProgramStatus.Completed)
                .limit(5000)
        ).valueChanges()).pipe(
            map((programs) => programs as MetalsLaserProgram[])
        ))).pipe(
            map(programGroups => Object.values(toEntityDict(programGroups.flat())))
        )
    }


    public getHighestLaserProgramNumber(): Observable<number> {
        return from(this.appwriteDb.collection<MetalsLaserProgram>(
            Collection.metalsLaserProgram,
            (ref) => ref
                .orderBy('programNumber', 'desc')
                .limit(1)
        ).get()).pipe(
            map((response) => {
                if (!!response.docs.length) {
                    return response.docs[0].data()?.programNumber ?? 0
                }
                return 0;
            })
        )
    }

    public setLaserProgramBaseFilePaths() {
        return from(this.appwriteDb.collection(Collection.globalVariables).doc(GlobalVariableDocumentIds.LaserProgramBaseFilePaths).set({
            'rZFNnZlwzTyJVeizn75a': 'J:/ENSIS3015/programs',
            'TyyHIU77rVsWvuWTXohq': 'J:/ORSUS3015/programs',
        }))
    }

    public setLaserAssetLabelPrinterIdDict() {
        return from(this.appwriteDb.collection(Collection.globalVariables).doc(GlobalVariableDocumentIds.LaserAssetLabelPrinterIdDict).set({
            'rZFNnZlwzTyJVeizn75a': 'sAoW44yQYl0nKeNfHxVZ',
            'TyyHIU77rVsWvuWTXohq': 'eTBiXSBLAHaQgFnBVZyG',
        }))
    }

    public loadLaserProgramBaseFilePaths(): Observable<Record<string, string>> {
        return from(this.appwriteDb.collection<Record<string, string>>(Collection.globalVariables).doc(GlobalVariableDocumentIds.LaserProgramBaseFilePaths).get()).pipe(
            map(resp => (fromAppwriteDoc(resp)))
        )
    }

    public loadLaserAssetLabelPrinterIdDict(): Observable<Record<string, string>> {
        return from(this.appwriteDb.collection<Record<string, string>>(Collection.globalVariables).doc(GlobalVariableDocumentIds.LaserAssetLabelPrinterIdDict).get()).pipe(
            map(resp => (fromAppwriteDoc(resp)))
        )
    }


    public loadMetalsLaserMaterials(): Observable<MetalsLaserMaterial[]> {
        return from(this.appwriteDb.collection<MetalsLaserMaterial>(Collection.metalsLaserMaterial).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadMetalsPowderCoatingPowders(): Observable<MetalsPowderCoatingPowder[]> {
        return from(this.appwriteDb.collection<MetalsPowderCoatingPowder>(Collection.metalsPowderCoatingPowder).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadMetalsPressBrakeBendRadii(): Observable<MetalsPressBrakeBendRadius[]> {
        return from(this.appwriteDb.collection<MetalsPressBrakeBendRadius>(Collection.metalsPressBrakeBendRadius).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public setMetalsLaserMaterial(material: MetalsLaserMaterial): Observable<void> {
        return from(this.appwriteDb.collection(Collection.metalsLaserMaterial).doc(material.id).set(material))
    }
    public setMetalsPowderCoatingPowder(powder: MetalsPowderCoatingPowder): Observable<void> {
        return from(this.appwriteDb.collection(Collection.metalsPowderCoatingPowder).doc(powder.id).set(powder))
    }
    public setMetalsPressBrakeBendRadius(radius: MetalsPressBrakeBendRadius): Observable<void> {
        return from(this.appwriteDb.collection(Collection.metalsPressBrakeBendRadius).doc(radius.id).set(radius))
    }

    public deleteLaserProgram(laserProgramId: string): Observable<void> {
        return from(this.appwriteDb.collection(Collection.metalsLaserProgram).doc(laserProgramId).delete())
    }

    public createLaserProgramsAndUpdateSchedule(
        programs: MetalsLaserProgram[],
        laserAssetId: string,
    ) {

        return this.loadLaserProgramScheduleDict().pipe(
            switchMap((dict: Record<string, MetalsLaserProgramSchedule>) => {
                const updatedDict: Record<string, MetalsLaserProgramSchedule> = {
                    ...dict,
                    [laserAssetId]: {
                        [MetalsLaserProgramScheduleShift.Night]: dict[laserAssetId][MetalsLaserProgramScheduleShift.Night],
                        [MetalsLaserProgramScheduleShift.Day]: [
                            ...dict[laserAssetId][MetalsLaserProgramScheduleShift.Day],
                            ...programs.map(p => p.id)
                        ],

                    }
                }

                return forkJoin([
                    ...programs.map((program) => this.setLaserProgramExistingOrCreate(program)),
                    this.setLaserProgramScheduleDict(updatedDict),
                ]).pipe(
                    switchMap(() => this.verifyLaserProgramsScheduled(
                        programs.map(program => program.id),
                        laserAssetId,
                        MetalsLaserProgramScheduleShift.Day,
                    )),
                    map(() => void 0)
                )

            })
        )


    }

    public setProject(project: MetalsProject): Observable<void> {
        return from(this.appwriteDb.collection<MetalsProject>(Collection.metalsProject).doc(project.id).set(project))
    }

    public setProjects(projects: MetalsProject[]): Observable<void[]> {
        return forkJoin(projects.map(t => this.setProject(t))).pipe(
            defaultIfEmpty([]),
        )
    }

    public setTask(task: MetalsTask): Observable<void> {
        return from(this.appwriteDb.collection<MetalsTask>(Collection.metalsTask).doc(task.id).set(task))
    }

    public setTasks(tasks: MetalsTask[]): Observable<void[]> {
        return forkJoin(tasks.map(t => this.setTask(t))).pipe(
            defaultIfEmpty([]),
        )
    }

    public recordPackLabelQuantity(
        projectId: string,
        taskId: string,
        quantity: number,
        stations: MetalsProcessStation[],
    ): Observable<MetalsTask> {
        return forkJoin({
            projectTasks: this.loadTasksByProjectId(projectId).pipe(first()),
            user: this.store.select(CoreSelectors.getCurrentUser).pipe(
                filter((user): user is User => !!user),
                first(),
            ),
        }).pipe(
            switchMap(({ projectTasks, user }) => {
                const task = projectTasks.find(candidate => candidate.id === taskId);
                if (!task) return throwError(() => new Error('task not found'));

                const updatedTask: MetalsTask = {
                    ...task,
                    completedQuantity: task.completedQuantity + quantity,
                    updatedById: user.id,
                    updatedOn: new Date(),
                };
                const changedTasks = new Map<string, MetalsTask>([[taskId, updatedTask]]);
                const tasksToActivate = determineTaskActivations(projectTasks, changedTasks, stations);
                return this.setTasks([updatedTask, ...tasksToActivate]).pipe(
                    map(() => updatedTask),
                );
            }),
        );
    }

    public loadProjectById(projectId: string): Observable<MetalsProject> {
        return from(this.appwriteDb.collection<MetalsProject>(Collection.metalsProject).doc(projectId).get()).pipe(
            map(resp => (fromAppwriteDoc(resp)))
        )
    }

    public loadProjectsByCustomerId(customerId: string): Observable<MetalsProject[]> {
        return from(this.appwriteDb.collection<MetalsProject>(
            Collection.metalsProject,
            (ref) => ref
                .where('customerId', '==', customerId)
                .limit(5000)
        ).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadProjectsByIds(projectIds: string[]): Observable<MetalsProject[]> {
        return this.appwriteDb.listByIds<MetalsProject>(Collection.metalsProject, projectIds);
    }

    public loadProjects(): Observable<MetalsProject[]> {
        return from(this.appwriteDb.collection<MetalsProject>(
            Collection.metalsProject,
            (ref) => ref.limit(5000)
        ).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadProjectsByStatuses(statuses: MetalsProjectStatus[]): Observable<MetalsProject[]> {
        return this.appwriteDb.collection<MetalsProject>(Collection.metalsProject, ref => ref
            .where('status', 'in', statuses)
            .limit(5000)
        ).get().pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadTaskById(taskId: string): Observable<MetalsTask> {
        return from(this.appwriteDb.collection<MetalsTask>(Collection.metalsTask).doc(taskId).get()).pipe(
            map(resp => (fromAppwriteDoc(resp)))
        )
    }

    public loadTasks(): Observable<MetalsTask[]> {
        return from(this.appwriteDb.collection<MetalsTask>(
            Collection.metalsTask,
            (ref) => ref.limit(5000)
        ).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadTasksByIds(taskIds: string[]): Observable<MetalsTask[]> {
        return this.appwriteDb.listByIds<MetalsTask>(Collection.metalsTask, taskIds);
    }

    public deleteTaskById(taskId: string): Observable<void> {
        return from(this.appwriteDb.collection<MetalsTask>(Collection.metalsTask).doc(taskId).delete());
    }

    public loadTasksByProjectId(projectId: string): Observable<MetalsTask[]> {
        return from(this.appwriteDb.collection<MetalsTask>(
            Collection.metalsTask,
            (ref: QueryRef) => ref
                .where('projectId', '==', projectId)
                .limit(5000)
        ).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadTasksByProjectIds(projectIds: string[]): Observable<MetalsTask[]> {
        return this.appwriteDb.listByFieldValues<MetalsTask>(Collection.metalsTask, 'projectId', projectIds);
    }

    public loadTasksByTypesAndStatuses(
        taskTypes: MetalsTaskType[],
        statuses: MetalsTaskStatus[],
    ): Observable<MetalsTask[]> {
        return from(this.appwriteDb.collection<MetalsTask>(
            Collection.metalsTask,
            (ref: QueryRef) => ref
                .where('status', 'in', statuses)
                .where('type', 'in', taskTypes)
                .limit(5000)
        ).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadTasksByStatuses(
        statuses: MetalsTaskStatus[],
    ): Observable<MetalsTask[]> {
        return from(this.appwriteDb.collection<MetalsTask>(
            Collection.metalsTask,
            (ref: QueryRef) => ref
                .where('status', 'in', statuses)
                .limit(5000)
        ).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public getTasksByTypesAndStatusesValueChanges(
        taskTypes: MetalsTaskType[],
        statuses: MetalsTaskStatus[],
    ): Observable<MetalsTask[]> {
        return from(this.appwriteDb.collection<MetalsTask>(
            Collection.metalsTask,
            (ref: QueryRef) => ref
                .where('status', 'in', statuses)
                .where('type', 'in', taskTypes)
                .limit(5000)
        ).valueChanges()).pipe(
            map(docs => docs as MetalsTask[])
        )
    }

    public getTasksByIdsValueChanges(taskIds: string[]): Observable<MetalsTask[]> {
        const uniqueTaskIds = Array.from(new Set(taskIds.filter(id => !!id)));

        if (uniqueTaskIds.length === 0) {
            return of([]);
        }

        const orderTasks = (tasks: MetalsTask[]): MetalsTask[] => {
            const taskDict = toEntityDict(tasks);
            return uniqueTaskIds
                .map(id => taskDict[id])
                .filter((task): task is MetalsTask => !!task);
        };

        // Appwrite query limits can change by server version; chunk defensively.
        const batchSize = 30;

        // If we have 30 or fewer IDs, simple query
        if (uniqueTaskIds.length <= batchSize) {
            return from(this.appwriteDb.collection<MetalsTask>(
                Collection.metalsTask,
                (ref: QueryRef) => ref
                    .where('$id', 'in', uniqueTaskIds)
                    .limit(uniqueTaskIds.length)
            ).valueChanges()).pipe(
                map(docs => orderTasks(docs as MetalsTask[]))
            );
        }

        // If more than 30 IDs, batch the queries
        const batches: string[][] = [];
        for (let i = 0; i < uniqueTaskIds.length; i += batchSize) {
            batches.push(uniqueTaskIds.slice(i, i + batchSize));
        }

        // Create an observable for each batch
        const batchObservables = batches.map(batch =>
            from(this.appwriteDb.collection<MetalsTask>(
                Collection.metalsTask,
                (ref: QueryRef) => ref
                    .where('$id', 'in', batch)
                    .limit(batch.length)
            ).valueChanges()).pipe(
                map(docs => docs as MetalsTask[])
            )
        );

        // Combine all batch results into a single array
        return combineLatest(batchObservables).pipe(
            map(results => orderTasks(results.flat()))
        );
    }

    public getTaskByIdValueChanges(
        taskId: string,
    ): Observable<MetalsTask> {

        return from(this.appwriteDb.collection<MetalsTask>(
            Collection.metalsTask,
        ).doc(taskId).valueChanges()).pipe(
            map(doc => {
                if (!doc) {
                    return null as unknown as MetalsTask;
                }
                return doc
            })
        )
    }

    public saveProject(
        currentTasks: MetalsTask[],
        deletedTasks: MetalsTask[],
        project: MetalsProject,
    ): Observable<void> {
        return forkJoin([
            ...currentTasks.map((task) => this.setTask(task)),
            this.setProject(project),
            ...deletedTasks.map((task) => this.deleteTaskById(task.id)),
        ]).pipe(
            map(() => void 0)
        )
    }

    public convertToStlFile() {

    }

    public uploadMetalsProjectFile(
        projectId: string,
        file: File,
    ): Observable<{
        metalsProjectFile: MetalsProjectFile;
        buffer: ArrayBuffer | null;
    }> {

        const fileId = this.appwriteDb.createId();
        const uploadPipe = pipe(
            map(snapshot => snapshot as CompatUploadSnapshot),
            filter(({ state }: CompatUploadSnapshot) => state === 'success')
        );

        const suffix = file.name.split('.')[1]

        const path = `${StorageFolder.metalsProject}/${projectId}/${fileId}.${suffix}`;

        return from(this.storage.upload(path, file)).pipe(
            uploadPipe,
            switchMap((imageUploadTask) => imageUploadTask.ref.getDownloadURL()),
            map((downloadUrl) => {

                let type: MetalsProjectFileType | null = null;

                const fileNameLower = file.name.toLowerCase();
                if (fileNameLower.endsWith('.dxf')) {
                    type = MetalsProjectFileType.DXF;
                } else if (fileNameLower.endsWith('.pdf')) {
                    type = MetalsProjectFileType.PDF;
                } else if (fileNameLower.endsWith('.stl')) {
                    type = MetalsProjectFileType.STL;
                } else if (fileNameLower.endsWith('.gltf')) {
                    type = MetalsProjectFileType.GLTF;
                } else if (fileNameLower.endsWith('.png')) {
                    type = MetalsProjectFileType.PNG;
                } else if (fileNameLower.endsWith('.jpg') || fileNameLower.endsWith('.jpeg')) {
                    type = MetalsProjectFileType.JPG;
                } else if (fileNameLower.endsWith('.stp') || fileNameLower.endsWith('.step')) {
                    type = MetalsProjectFileType.STEP;
                } else if (fileNameLower.endsWith('.igs') || fileNameLower.endsWith('.iges')) {
                    type = MetalsProjectFileType.IGS;
                } else if (fileNameLower.endsWith('.dwg') || fileNameLower.endsWith('.DWG')) {
                    type = MetalsProjectFileType.DWG;
                } else if (fileNameLower.endsWith('.bendconfig.json')) {
                    // Checked before .json would be, so a bend config is never
                    // mistaken for some other payload the station cannot render
                    type = MetalsProjectFileType.BendConfig;
                } else {
                    console.warn(`Ignored unsupported file: ${file.name}`);
                }

                if (type === null) {
                    return null as unknown as MetalsProjectFile
                }

                const metalsProjectFile: MetalsProjectFile = {
                    id: fileId,
                    name: file.name,
                    type,
                    downloadUrl,
                    size: file.size
                }
                return metalsProjectFile;


            }),
            switchMap((metalsProjectFile) => {
                if (metalsProjectFile.type !== MetalsProjectFileType.DXF) {
                    return of({ metalsProjectFile, buffer: null });
                }

                return new Observable<ArrayBuffer>(observer => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        observer.next(reader.result as ArrayBuffer);
                        observer.complete();
                    };
                    reader.onerror = (err) => observer.error(err);
                    reader.readAsArrayBuffer(file);
                }).pipe(
                    map(buffer => ({ metalsProjectFile, buffer })) // Wrap in array
                );
            }),
        )
    }

    public convertCadFileToGltf(cadFile: MetalsProjectFile, projectId: string): Observable<MetalsProjectFile> {
        return defer(() => {
            const outputFileId = cadFileIdToGltfFileId(cadFile.id);
            return this.functions.postAsyncAndWait(
                'api',
                `/${AppwriteApiRoute.Utility}/${AppwriteApiEndpoint.ConvertStepToGltf}`,
                { file: cadFile, projectId, outputFileId },
            ).pipe(
                switchMap(() => this.loadConvertedCadFile(cadFile, projectId)),
            );
        });
    }

    public findConvertedCadFile(cadFile: MetalsProjectFile, projectId: string): Observable<MetalsProjectFile | null> {
        return defer(() => {
            const outputFileId = cadFileIdToGltfFileId(cadFile.id);
            return this.storage.findFileMetadata$(StorageFolder.metalsProject, outputFileId).pipe(
                map(metadata => metadata
                    ? this.convertedCadFileFromMetadata(cadFile, projectId, metadata)
                    : null
                ),
            );
        });
    }

    private loadConvertedCadFile(cadFile: MetalsProjectFile, projectId: string): Observable<MetalsProjectFile> {
        return defer(() => {
            const outputFileId = cadFileIdToGltfFileId(cadFile.id);

            return this.storage.getFileMetadata$(StorageFolder.metalsProject, outputFileId).pipe(
                map(metadata => this.convertedCadFileFromMetadata(cadFile, projectId, metadata)),
            );
        });
    }

    private convertedCadFileFromMetadata(
        cadFile: MetalsProjectFile,
        projectId: string,
        metadata: { id: string; name: string; mimeType: string; sizeOriginal: number },
    ): MetalsProjectFile {
        const outputFileId = cadFileIdToGltfFileId(cadFile.id);
        const convertedName = cadFileNameToGltfFileName(cadFile.name);
        const expectedStoredName = `${projectId}/${convertedName}`;

        if (metadata.id !== outputFileId
            || metadata.name !== expectedStoredName
            || metadata.mimeType !== 'model/gltf-binary'
            || metadata.sizeOriginal < 20
        ) {
            throw new Error('The converted CAD preview file did not match the requested source file.');
        }

        return {
            id: outputFileId,
            name: convertedName,
            type: MetalsProjectFileType.GLTF,
            downloadUrl: this.storage.getFileViewUrl(StorageFolder.metalsProject, outputFileId),
            size: metadata.sizeOriginal,
        };
    }



    public loadProcessStations(): Observable<MetalsProcessStation[]> {
        return from(this.appwriteDb.collection<MetalsProcessStation>(Collection.metalsProcessStation).get()).pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public getProcessStationsValueChanges(): Observable<MetalsProcessStation[]> {
        return from(this.appwriteDb.collection<MetalsProcessStation>(Collection.metalsProcessStation).valueChanges()).pipe(
            map(docs => docs as MetalsProcessStation[])
        )
    }

    public loadProcessStationById(stationId: string): Observable<MetalsProcessStation> {
        return from(this.appwriteDb.collection<MetalsProcessStation>(Collection.metalsProcessStation).doc(stationId).get()).pipe(
            map(resp => (fromAppwriteDoc(resp)))
        )
    }

    public getProcessStationByIdValueChanges(stationId: string): Observable<MetalsProcessStation> {
        return from(this.appwriteDb.collection<MetalsProcessStation>(Collection.metalsProcessStation)
            .doc(stationId).valueChanges()).pipe(
                map(doc => {
                    if (!doc) {
                        return null as unknown as MetalsProcessStation;
                    }
                    return doc;
                })
            )
    }

    public setProcessStation(station: MetalsProcessStation): Observable<void> {
        return from(this.appwriteDb.collection<MetalsProcessStation>(Collection.metalsProcessStation).doc(station.id).set(station));
    }

    public setProcessStations(stations: MetalsProcessStation[]): Observable<void[]> {
        return forkJoin(stations.map(s => this.setProcessStation(s))).pipe(
            defaultIfEmpty([])
        )
    }

    public setTaskQuantityChangeEvent(event: MetalsTaskQuantityChangeEvent): Observable<void> {
        return from(this.appwriteDb.collection<MetalsTaskQuantityChangeEvent>(Collection.metalsTaskQuantityChangeEvent).doc(event.id).set(event))
    }

    public loadOrderedProductsByStatuses(
        statuses: MetalsOrderedProductStatus[],
        oldestDate: Date | null = null,
    ): Observable<MetalsOrderedProduct[]> {

        let query;

        if (!!oldestDate) {
            query = (ref: QueryRef) => ref
                .where('status', 'in', statuses)
                .where('updatedOn', '>', oldestDate)
        } else {
            query = (ref: QueryRef) => ref
                .where('status', 'in', statuses)
        }

        return this.appwriteDb.collection<MetalsOrderedProduct>(Collection.metalsOrderedProduct, query
        ).get().pipe(
            map(resp => (fromAppwriteDocs(resp.docs))),
        )
    }

    public loadOrderedProductById(orderedProductId: string): Observable<MetalsOrderedProduct> {
        return this.appwriteDb.collection<MetalsOrderedProduct>(Collection.metalsOrderedProduct).doc(orderedProductId).get().pipe(
            map(resp => (fromAppwriteDoc(resp)))
        )
    }

    public loadOrderedProductAndOrderByOrderedProductId(orderedProductId: string): Observable<{ orderedProduct: MetalsOrderedProduct, order: MetalsOrder }> {
        return this.loadOrderedProductById(orderedProductId).pipe(
            switchMap(orderedProduct => {
                return this.loadOrderById(orderedProduct.metalsOrderId).pipe(
                    map(order => ({ orderedProduct, order }))
                )
            })
        )
    }

    public loadOrderedProductsByIds(orderedProductIds: string[]): Observable<MetalsOrderedProduct[]> {
        return this.appwriteDb.listByIds<MetalsOrderedProduct>(Collection.metalsOrderedProduct, orderedProductIds);
    }

    public loadOrderedProductsByOrderId(orderId: string): Observable<MetalsOrderedProduct[]> {
        return this.appwriteDb.list<MetalsOrderedProduct>(Collection.metalsOrderedProduct, [
            Query.equal('metalsOrderId', orderId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items.map(orderedProduct => ({
                ...orderedProduct,
                changeLog: orderedProduct.changeLog.sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime()),
            }))),
        );
    }

    public loadOrderById(orderId: string): Observable<MetalsOrder> {
        return from(this.appwriteDb.collection<MetalsOrder>(Collection.metalsOrder).doc(orderId).get()).pipe(
            map(resp => (fromAppwriteDoc(resp)))
        )
    }
    public loadOrderByIdValueChanges(orderId: string): Observable<MetalsOrder> {
        return this.appwriteDb.collection<MetalsOrder>(Collection.metalsOrder).doc(orderId).valueChanges().pipe(
            map(resp => {
                if (!resp) return null as unknown as MetalsOrder

                return resp
            })
        )
    }

    public loadOrdersByIds(orderIds: string[]): Observable<MetalsOrder[]> {
        return this.appwriteDb.listByIds<MetalsOrder>(Collection.metalsOrder, orderIds);
    }

    public getOrderCount(): Observable<number> {
        return this.appwriteDb.collection<MetalsOrder>(Collection.metalsOrder).count();
    }

    public setOrder(order: MetalsOrder): Observable<void> {
        return from(this.appwriteDb.collection<MetalsOrder>(Collection.metalsOrder).doc(order.id).set(order))
    }

    public getMetalsOrderedProductsByOrderIdValueChanges(metalsOrderId: string): Observable<MetalsOrderedProduct[]> {
        return this.appwriteDb.collection<MetalsOrderedProduct>(Collection.metalsOrderedProduct, ref => ref
            .where('metalsOrderId', '==', metalsOrderId)
        ).valueChanges().pipe(
            map(orderedProducts => orderedProducts.map(op => ({
                ...op,
                changeLog: op.changeLog.sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime())
            })))
        )
    }

    public setOrderedProduct(orderedProduct: MetalsOrderedProduct): Observable<void> {
        return from(this.appwriteDb.collection<MetalsOrderedProduct>(Collection.metalsOrderedProduct).doc(orderedProduct.id).set(orderedProduct))
    }

    public setOrderedProducts(orderedProducts: MetalsOrderedProduct[]): Observable<void> {
        return forkJoin(orderedProducts.map((orderedProduct) => this.setOrderedProduct(orderedProduct))).pipe(
            map(() => void 0),
            defaultIfEmpty(void 0),
        );
    }

    public deleteOrder(orderId: string): Observable<void> {
        return from(this.appwriteDb.collection<MetalsOrder>(Collection.metalsOrder).doc(orderId).delete())
    }

    public getOrderedProductsByProductionPlanCompleteAndStatusValueChanges(productionPlanComplete: boolean, statuses: MetalsOrderedProductStatus[]): Observable<MetalsOrderedProduct[]> {
        return this.appwriteDb.collection<MetalsOrderedProduct>(Collection.metalsOrderedProduct, ref => ref
            .where('productionPlanComplete', '==', productionPlanComplete)
            .where('status', 'in', statuses)

        ).valueChanges().pipe(
            map(resp => resp as MetalsOrderedProduct[]),
        )
    }

    public loadReleasedAndFinishedProductVersions(): Observable<MetalsProductVersion[]> {
        return this.appwriteDb.collection<MetalsProductVersion>(Collection.metalsProductVersion, ref => ref
            .where('status', '==', MetalsProductVersionStatus.Released)
            .where('type', '==', MetalsProductType.Finished)
        ).get().pipe(
            map((resp) => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadOrders(): Observable<MetalsOrder[]> {
        return this.appwriteDb.collection<MetalsOrder>(Collection.metalsOrder).get().pipe(
            map(resp => (fromAppwriteDocs(resp.docs)))
        )
    }

    public loadProjectByOrderedProductId(orderedProductId: string): Observable<MetalsProject> {
        return this.appwriteDb.collection<MetalsProject>(Collection.metalsProject, ref => ref
            .where('orderedProductId', '==', orderedProductId)
        ).get().pipe(
            map(resp => {
                if (!resp.docs.length) return null as unknown as MetalsProject;

                return (fromAppwriteDocs(resp.docs))[0]
            })
        )
    }

    public getProjectByOrderedProductIdValueChanges(orderedProductId: string): Observable<MetalsProject> {
        return this.appwriteDb.collection<MetalsProject>(Collection.metalsProject, ref => ref
            .where('orderedProductId', '==', orderedProductId)
        ).valueChanges().pipe(
            map(resp => {
                if (!resp.length) return null as unknown as MetalsProject;

                return resp[0]
            })
        )
    }

    // Metals Quote Requests (NOT PERSISTED — feature is in-memory only for now)

    public loadMetalsQuoteRequests(): Observable<MetalsQuoteRequest[]> {
        return of([]);
    }

    public loadMetalsQuoteRequestById(_id: string): Observable<MetalsQuoteRequest> {
        return of(null as unknown as MetalsQuoteRequest);
    }

    public setMetalsQuoteRequest(_request: MetalsQuoteRequest): Observable<void> {
        return of(undefined);
    }

    // ─── Label Instances ──────────────────────────────────────────────────

    public loadLabelInstanceById(id: string): Observable<LabelInstance> {
        return this.appwriteDb.collection<LabelInstance>(Collection.plasticsLabelInstance).doc(id).get().pipe(
            map((resp) => (fromAppwriteDoc(resp)))
        )
    }

    public getObsessedEngineeringProductLabelInstancesByOrderedProductIdsValueChanges(
        orderedProductIds: string[],
    ): Observable<LabelInstance_ObsessedEngineeringProduct[]> {
        const batches = chunkArray(Array.from(new Set(orderedProductIds.filter((id) => !!id))), 30);
        if (!batches.length) {
            return of([]);
        }

        return combineLatest(batches.map((batch) => this.appwriteDb.collection<LabelInstance>(
            Collection.plasticsLabelInstance,
            (ref) => ref.where('orderedProductId', 'in', batch),
        ).valueChanges().pipe(
            map((labels) => labels.filter((label): label is LabelInstance_ObsessedEngineeringProduct => (
                label.type === LabelType.ObsessedEngineeringProduct
            ))),
        ))).pipe(
            map((labelGroups) => labelGroups.flat()),
        );
    }

    public loadLabelInstancesByOrderedProductId(
        orderedProductId: string,
    ): Observable<LabelInstanceWithOrderedProductId[]> {
        return this.appwriteDb.collection<LabelInstance>(
            Collection.plasticsLabelInstance,
            (ref) => ref.where('orderedProductId', '==', orderedProductId),
        ).get().pipe(
            map((response) => fromAppwriteDocs(response.docs).filter((label): label is LabelInstanceWithOrderedProductId => (
                'orderedProductId' in label
            ))),
        );
    }

    public unlinkObsessedEngineeringProductLabelInstancesByOrderedProductId(
        orderedProductId: string,
    ): Observable<number> {
        return this.loadLabelInstancesByOrderedProductId(orderedProductId).pipe(
            switchMap((labelInstances) => {
                if (!labelInstances.length) {
                    return of(0);
                }

                return forkJoin(labelInstances.map((labelInstance) => this.setLabelInstance({
                    ...labelInstance,
                    orderedProductId: '',
                }))).pipe(
                    map(() => labelInstances.length),
                );
            }),
        );
    }

    public setLabelInstance(labelInstance: LabelInstance): Observable<void> {
        return from(this.appwriteDb.collection<LabelInstance>(Collection.plasticsLabelInstance).doc(labelInstance.id).set(labelInstance))
    }

    /** Active projects with this code. Codes are short and random with no uniqueness
     *  check, so old projects can share one; callers pick when more than one matches. */
    public loadActiveProjectsByCode(code: string): Observable<MetalsProject[]> {
        return this.appwriteDb.collection<MetalsProject>(Collection.metalsProject, ref =>
            ref.where('code', '==', code).where('status', '==', MetalsProjectStatus.Active).limit(10)
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        )
    }

    // Project photos. They live in the metals-project row's `files` column, which
    // fromAppwrite never puts on a MetalsProject, so whole-project saves cannot write
    // back a stale list. Only these methods read or write it, re-reading right before.

    private projectFilesTables: TablesDB | null = null;

    private get rawTables(): TablesDB {
        return this.projectFilesTables ??= new TablesDB(this.appwriteDb.client);
    }

    /** Photos and other project-level files, newest first. */
    public loadProjectFiles(projectId: string): Observable<MetalsProjectFile[]> {
        return defer(() => this.readProjectFiles(projectId)).pipe(
            map(files => [...files].sort((a, b) => +(b.uploadedOn ?? 0) - +(a.uploadedOn ?? 0))),
        );
    }

    /** Appends one file (already uploaded with uploadMetalsProjectFile). Returns the new list. */
    public addProjectFile(projectId: string, file: MetalsProjectFile): Observable<MetalsProjectFile[]> {
        const stamped: MetalsProjectFile = {
            ...file,
            uploadedOn: file.uploadedOn ?? new Date(),
            uploadedById: file.uploadedById || this.currentUserId,
        };
        return defer(async () => {
            const files = await this.readProjectFiles(projectId);
            const next = files.some(existing => existing.id === stamped.id) ? files : [...files, stamped];
            await firstValueFrom(this.appwriteDb.update(Collection.metalsProject, projectId, { files: next }));
            return next;
        });
    }

    /** Removes one file from the project and deletes it from storage. Returns the new list. */
    public removeProjectFile(projectId: string, file: MetalsProjectFile): Observable<MetalsProjectFile[]> {
        return defer(async () => {
            const files = await this.readProjectFiles(projectId);
            const next = files.filter(existing => existing.id !== file.id);
            await firstValueFrom(this.appwriteDb.update(Collection.metalsProject, projectId, { files: next }));
            await firstValueFrom(this.discardProjectFileUpload(projectId, file));
            return next;
        });
    }

    /** Deletes an uploaded project file from storage only (e.g. when attaching it failed). Never errors. */
    public discardProjectFileUpload(projectId: string, file: MetalsProjectFile): Observable<void> {
        // Same path uploadMetalsProjectFile wrote to.
        const path = `${StorageFolder.metalsProject}/${projectId}/${file.id}.${file.name.split('.')[1]}`;
        return this.storage.ref(path).delete().pipe(
            catchError(err => {
                console.warn('project file was not deleted from storage', err);
                return of(undefined);
            }),
        );
    }

    private async readProjectFiles(projectId: string): Promise<MetalsProjectFile[]> {
        const row = await this.rawTables.getRow({
            databaseId: this.appwriteDb.databaseId,
            tableId: Collection.metalsProject,
            rowId: projectId,
        });
        const stored: unknown = (row as Record<string, unknown>)['files'];
        return (Array.isArray(stored) ? stored : [])
            .filter((item): item is string => typeof item === 'string' && !!item.trim())
            .map(item => JSON.parse(item) as MetalsProjectFile)
            .map(file => ({ ...file, uploadedOn: file.uploadedOn ? new Date(file.uploadedOn) : null }));
    }
}
